import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
  icon,
} from '@Root/lib/lit-3form-block';
import MicroModal from 'micromodal';
import { styles } from './css';
import { a11yStyles } from '../../form-controls/a11y-styles';
import './barrel';
import './customize-product-barrel';
import './customize-material-barrel';
import './order-sheet-barrel';
import './estimate-barrel';
import './cut-to-order-barrel';
import './customize-choices-barrel';
import './material-details-block';
import './product-details-block';
import './graphic-pattern-designer-barrel';
import './digital-print-designer-barrel';
import { nothing } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import {
  downloadBlob,
  exportPng,
  previewClassName,
  previewStyle,
  renderToCanvas,
} from '@Helpers/pattern-recolor';

import {
  fetchCart,
  checkProductAvailability,
  addToCart,
} from '@Root/spree/api';
import {
  updateCartIndicator,
  changeProductNameInModal,
  changeProductSizeInfoInModal,
  changeProductImageInModal,
} from '@Root/spree/dom';
import {
  useAddToCartModal,
  useCartConfirmationModal,
  waitForElement,
  isDealerPage,
  logErrorToSentry,
  getPageVariable,
} from '../../../helpers/helpers';
import { isMobileLayout } from '../../../helpers/layout';
import { pushDataLayerEvent } from '../../../helpers/data-layer';
import { createToolAnalytics } from '../../../helpers/tool-analytics';
import {
  handleFileDownloadEvent,
  analyticsCtaDataObject,
  analyticsLabelFromString,
  adobeCleanString,
  fireAdobeAnalyticsEvent,
  handleAdobeNavEvent,
} from '../../../helpers/adobe-analytics';
import { vimeoIdFrom } from '@Helpers/vimeo-video';
import {
  BitreelViewerClient,
  VIEWER_CAPABILITIES,
  viewerConfigFrom,
  viewerPatternImageFrom,
} from '@Helpers/bitreel-viewer';
import { BitreelFrameClient } from '@Helpers/bitreel-frame-client';
import {
  EMBED_IMAGE_FIT,
  VIEWER_PREFIX as DIGITAL_PRINT_VIEWER_PREFIX,
  urlAtFit,
} from '@Helpers/digital-print-designer';
import { dataUrlFrom, storeDesignFile } from '@Helpers/digital-print-files';

import find from 'lodash.find';

// Thumbnail for the 3D viewer slide until Bitreel supplies art.
const VIEWER_THUMB_SRC = '/static/images/graphic-pattern-3d-thumb.svg';
// The viewer is about 4 MB; past this, a viewer that has not posted ready is
// treated as blocked or broken.
const VIEWER_READY_TIMEOUT_MS = 30000;
// Width of the configured panel handed to the quote form (panels are 1:2).
const QUOTE_IMAGE_WIDTH = 400;
// Pattern pages go back here; the catalog reads its color from the same
// 'pattern' query param the designer writes.
const GRAPHIC_PATTERNS_CATALOG_PATH = '/graphic-patterns';
// Written by a catalog card click: that catalog's path with its filters and
// search.
const GRAPHIC_PATTERNS_RETURN_URL_KEY = 'graphicPatternsReturnUrl';

// Source height for the zoomed hero: the tallest carousel box (860px) at the
// 2x in --carousel-zoom-scale, so a zoomed image resolves detail rather than
// enlarging the pixels of the display-sized render.
const CAROUSEL_ZOOM_IMAGE_HEIGHT = 1720;

const imageSelectHashPrefix = '#w-image-';
const imageSelectHashRegExp = new RegExp(`${imageSelectHashPrefix}(\\d+)`);

// Material tabs reload the page scrolled back to the Material Details block.
const MATERIAL_DETAILS_HASH = 'material-details';

// Every barrel carries the designer flags as the string Avo submits, and '0'
// is truthy here, so the serialized designer payload is the only safe
// signal. Without it the barrel has nothing to render anyway.
const barrelPayloadFrom = key => barrel => {
  const payload = barrel && barrel[key];
  return payload && typeof payload === 'object' ? payload : null;
};
const designerContentFrom = barrelPayloadFrom('graphic_pattern_designer');
const digitalPrintContentFrom = barrelPayloadFrom('digital_print_designer');

const DIGITAL_PRINT_TOOL_NAME = 'Digital Print Designer';
// Library images on the digital print image slide, resized by PhotoShelter.
const DIGITAL_PRINT_HERO_FIT = 1000;
const DIGITAL_PRINT_THUMB_FIT = 200;
// A frame the browser has stopped painting may never answer a capture; the
// quote goes without one rather than keep the visitor waiting.
const DIGITAL_PRINT_QUOTE_CAPTURE_TIMEOUT_MS = 5000;
// Every lead's image has the same landscape shape, whatever the carousel's.
const DIGITAL_PRINT_QUOTE_CAPTURE_SIZE = { width: 1600, height: 1000 };

// Uploads have no image number to name their files after.
const digitalPrintFilename = (imageNumber, suffix) => {
  const base = imageNumber ? `3FORM-${imageNumber}` : 'digital-print-design';
  return `${base}-${suffix}`;
};

export class Walter extends Lit3formBlock {
  static get characterName() {
    return 'walter';
  }

  static get characterStyles() {
    return [...styles, a11yStyles];
  }

  static properties = {
    _selectedCarouselImageId: {
      type: Number,
      state: true,
      hasChanged: (newValue, oldValue) => {
        const hasChanged = newValue !== oldValue;

        if (hasChanged && window.location.hash.match(imageSelectHashRegExp))
          window.location.hash = `${imageSelectHashPrefix}${newValue + 1}`;

        return hasChanged;
      },
    },
    _carouselHeight: { type: Number },
    _barrels: { type: Array },
    _showMaterialDetailsBlockOnProductPage: { type: Boolean },
    _materialDetailsBlockData: { type: Array },
    _productDetailsBlockData: { type: Array },
    _variants: { type: Array },
    _materialVariants: { type: Array },
    includedPropertiesById: { type: Object },
    primaryCtaHidden: { type: Boolean },
    primaryCtaDisabled: { type: Boolean },
    _hasCustomizeBarrel: { type: Boolean },
    _hasOrderSheetBarrel: { type: Boolean },
    _camera1ImageUrl: { type: String },
    _selectedCustomPattern: { type: Object },
    _selectedHardwareFinish: { type: Object },
    _barrelSelectedCarouselImages: { type: Array },
    _renderedProductImagesForMaterial: { type: Array },
    _carouselImagesArray: { type: Array },
    _loadedThreekitImages: { type: Object },
    _isEstimate: { type: Boolean },
    _addToCartQuantity: { type: Number },
    _showMobileStickyHeader: { type: Boolean },
    _carouselZoomEnabled: { type: Boolean },
    _carouselZoomOrigin: { type: Object },
    _carouselZoomSrc: { type: String },
    _fetchingNewCarouselImage: { type: Boolean },
    _viewerStatus: { type: String },
    _viewerLoading: { type: Boolean },
    _viewerFailed: { type: Boolean },
    _exporting: { type: Boolean },
    _pointerDragging: { type: Boolean },
  };

  constructor() {
    super();

    this._carouselImagesArray = this.content.images || [];

    const matches = window.location.hash.match(imageSelectHashRegExp),
      imageIndex = matches && matches[1] - 1;

    if (
      typeof imageIndex === 'number' &&
      imageIndex <= this._carouselImagesArray.length
    )
      this._selectedCarouselImageId = imageIndex;
    else this._selectedCarouselImageId = 0;

    this.formatBarrels(this.content.barrels);

    this._availableSampleTypes =
      typeof this.content.sample_types === 'object' &&
      Object.keys(this.content.sample_types).length > 0;

    if (!!this.content.sample_types)
      this.availableSampleTypes = Object.keys(this.content.sample_types);
    else this.availableSampleTypes = [];

    this._materialVariants = [];
    this._currentMaterialName = '';

    this.primaryCtaHidden = false;
    this.primaryCtaDisabled = !this.content.cta?.url
      ?.toLowerCase()
      .includes('visualizer');
    this._hasCustomizeBarrel = false;
    this._hasOrderSheetBarrel = false;
    this._camera1ImageUrl = '';
    this._addToCartQuantity = 1;
    this._showMobileStickyHeader = false;
    this._carouselZoomEnabled = false;
    this._carouselZoomOrigin = { x: 50, y: 50 };
    this._carouselZoomSrc = '';
    this._carouselZoomFrame = null;
    this._carouselZoomPointer = { clientX: 0, clientY: 0 };
    // Real values are read off the custom properties when zoom starts.
    this._carouselZoomScale = 1;
    this._carouselZoomPadding = 0;
    this._panelImageId = 'panel_image';
    this._fetchingNewCarouselImage = false;
    this._showMaterialDetailsBlockOnProductPage = false;
    this._viewer = null;
    this._viewerStatus = '';
    this._viewerLoading = false;
    this._viewerFailed = false;
    this._viewerReadyTimer = 0;
    this._viewerLoadAllowed = false;
    this._loadingMaskUrls = new Set();
    this._loadedMaskUrls = new Set();
    this._exporting = false;
    this._quoting = false;
    this._digitalPrint = null;
    this._pointerDragging = false;
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._handleScroll = this._handleScroll.bind(this);

    this._isEstimate = this._barrels.some(
      barrel => barrel.hasOwnProperty('estimate') && !!barrel.estimate
    );

    this.addEventListener('customPatternSelected', e => {
      this._selectedCustomPattern = e.detail?.option || {};
      this.updateCarouselImages();
    });

    this.addEventListener('hardwareColorSelected', e => {
      this._selectedHardwareFinish = e.detail.option || {};
      this.updateCarouselImages();
    });

    this.addEventListener('newCarouselImagesFromBarrel', e => {
      this._barrelSelectedCarouselImages = e.detail.selectionImages || [];
      this.addThreekitImages(this._barrelSelectedCarouselImages);
    });

    this.addEventListener('productSelectedForMaterial', e => {
      this._renderedProductImagesForMaterial = e.detail.renderedImages || [];
      this.addThreekitImages(this._renderedProductImagesForMaterial, true);
    });

    this.addEventListener('requestMoreInfoFromBarrel', e => {
      this.requestMoreInfoClicked(e);
    });

    this.addEventListener('urlScrollToVisualize', e => {
      this.scrollToVisualize();
    });

    this.addEventListener('expandedOrderSheetBarrel', e => {
      this.selectCarouselImageFromDescription('panel');
      this.openOrderSheetDialog();
    });

    this.addEventListener('useWalterOrderSample', e => {
      this.openSampleOrderModal();
    });

    this.addEventListener('useWalterFallbackCta', e => {
      this.requestMoreInfoClicked(e);
    });

    this.addEventListener('fetchingNewCarouselImage', e => {
      this._fetchingNewCarouselImage = true;
    });

    this.addEventListener('graphicPatternDesignerUpdated', e => {
      this.updateDesignerSlide(e.detail);
    });

    this.addEventListener('digitalPrintDesignerUpdated', e => {
      this.updateDigitalPrintDesigner(e.detail);
    });

    this.addEventListener('downloadConfiguredImageFromBarrel', e => {
      this.downloadConfiguredImageForBarrel(e);
    });

    // Temporary workaround. This can be removed once the Bitreel software is
    // no longer pushing full sized images to this cookie.
    localStorage.removeItem('requestInfoData');

    this._loadedThreekitImages = {};
  }

  async connectedCallback() {
    super.connectedCallback();

    this.addVariants();

    if (!!this.content.vimeo_video_url) {
      this.addVideoToCarousel();
    }

    if (!!this.content.imajize_id) {
      this.addSpinshotToCarousel();
    }

    if (this._isEstimate) {
      this.selectCarouselImageFromDescription('panel');
    }

    window.addEventListener('scroll', this._handleScroll, false);
    this.addEventListener('pointerdown', this._onPointerDown);
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerUp);
    document.addEventListener(
      'layout-shift-required',
      this._handleScroll,
      false
    );
    // Initial trigger in case the page loads in a scrolled position
    this._handleScroll();

    try {
      await this.setupSpreeData();
    } catch (error) {
      this.primaryCtaHidden = true;
      logErrorToSentry(error);
    }

    window.JSREPORT_READY_TO_START = true;
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this._handleScroll, false);
    document.removeEventListener(
      'layout-shift-required',
      this._handleScroll,
      false
    );
    this.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerUp);
    if (this._carouselZoomFrame) cancelAnimationFrame(this._carouselZoomFrame);
    window.clearTimeout(this._viewerReadyTimer);
    this._viewer?.dispose();
    this._viewer = null;
    super.disconnectedCallback();
  }

  // A drag that starts outside the viewer (the color picker) would end the
  // moment the pointer crossed the iframe, which swallows pointer events;
  // the frame is shielded until the pointer is released.
  _onPointerDown(event) {
    const path = event.composedPath();
    const frame = this.shadowRoot.getElementById('viewer-3d-frame');
    if (frame && path.includes(frame)) return;
    this._pointerDragging = true;
  }

  _onPointerUp() {
    this._pointerDragging = false;
  }

  async _handleScroll() {
    if (!isMobileLayout()) {
      this._showMobileStickyHeader = false;
      return;
    }
    this.endCarouselZoom();
    const walterBlockEl = await waitForElement('walter-block');
    if (!walterBlockEl) return;
    const stickyWrapperEl = walterBlockEl.shadowRoot.getElementById(
        'sticky-hero-wrapper-mobile'
      ),
      heroMediaEl = walterBlockEl.shadowRoot.getElementById('hero-media');
    if (!heroMediaEl) return;
    const stickyWrapperHeight = stickyWrapperEl
        ? stickyWrapperEl.getBoundingClientRect().height
        : 0,
      headerBottomPosition = heroMediaEl.getBoundingClientRect().bottom;

    const currentStatus = this._showMobileStickyHeader ? true : false;
    this._showMobileStickyHeader = headerBottomPosition <= stickyWrapperHeight;
    // apply logic only when _showMobileStickyHeader goes from false to true
    if (!currentStatus && this._showMobileStickyHeader) {
      if (this.isEmbeddedSlide(this.selectedCarouselImage)) {
        this.changeCarouselImage('next');
      }
    }

    const walterBox = walterBlockEl.getBoundingClientRect(),
      viewableWalter = walterBox?.height + walterBox?.y || 0;
    if (viewableWalter <= stickyWrapperHeight) {
      stickyWrapperEl.style['top'] = `${-Math.abs(
        stickyWrapperHeight - viewableWalter
      )}px`;
    } else {
      stickyWrapperEl.style['top'] = '0px';
    }
  }

  // Click-to-zoom on the hero image: the point under the cursor becomes the
  // transform origin, so that point stays put while the image scales up and
  // moving the cursor pans across the image.
  get carouselZoomable() {
    const image = this.selectedCarouselImage;
    return !isMobileLayout() && !!image && !this.isEmbeddedSlide(image);
  }

  toggleCarouselZoom(event) {
    if (!this.carouselZoomable) return;

    if (this._carouselZoomEnabled) {
      this.endCarouselZoom();
      return;
    }

    this.startCarouselZoom();
    this.trackCarouselZoomOrigin(event);
    // Keeps Escape and the arrow keys on the zoomed image rather than the page.
    event.currentTarget?.focus?.();
  }

  startCarouselZoom() {
    const tokens = getComputedStyle(this.heroImageBox);
    this._carouselZoomScale =
      parseFloat(tokens.getPropertyValue('--carousel-zoom-scale')) || 1;
    this._carouselZoomPadding =
      parseFloat(tokens.getPropertyValue('--carousel-zoom-padding')) || 0;

    this._carouselZoomEnabled = true;
    this.loadCarouselZoomImage();
    this.sendAnalyticsCarouselImageZoom();
    this.selectedImageToolAnalytics?.view('Zoom');
  }

  endCarouselZoom() {
    if (!this._carouselZoomEnabled) return;

    if (this._carouselZoomFrame) {
      cancelAnimationFrame(this._carouselZoomFrame);
      this._carouselZoomFrame = null;
    }
    this._carouselZoomEnabled = false;
    this._carouselZoomOrigin = { x: 50, y: 50 };
    this._carouselZoomSrc = '';
  }

  // mousemove fires far more often than the browser paints; hold the latest
  // position and read it once per frame.
  carouselZoomMoved(event) {
    if (!this._carouselZoomEnabled) return;

    this._carouselZoomPointer = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    if (this._carouselZoomFrame) return;

    this._carouselZoomFrame = requestAnimationFrame(() => {
      this._carouselZoomFrame = null;
      this.trackCarouselZoomOrigin(this._carouselZoomPointer);
    });
  }

  // Measured against the hero box, which never carries the transform itself, so
  // the origin stays correct while zoomed and after a scroll or layout shift.
  trackCarouselZoomOrigin({ clientX, clientY }) {
    const box = this.heroImageBox?.getBoundingClientRect();
    if (!box?.width || !box?.height) return;

    const media = this.heroImageElement;
    this._carouselZoomOrigin = {
      x: this.carouselZoomOrigin(
        clientX - box.left,
        box.width,
        media?.offsetWidth
      ),
      y: this.carouselZoomOrigin(
        clientY - box.top,
        box.height,
        media?.offsetHeight
      ),
    };
  }

  // The origin the cursor maps to on one axis, as a percentage of the hero's
  // own box — transform-origin resolves against that, not the container.
  carouselZoomOrigin(pointer, boxSize, contentSize) {
    const scale = this._carouselZoomScale,
      padding = this._carouselZoomPadding;
    if (scale <= 1 || !contentSize) return 50;

    // Scaled up the hero still clears the margin on this axis, so there is
    // nothing to pan to and sliding it would only push it out of the frame.
    const scaled = scale * contentSize;
    if (scaled + 2 * padding <= boxSize) return 50;

    // Travel of the hero's own leading edge, in box pixels. It stops
    // --carousel-zoom-padding short of flush; the axis the hero letterboxes on
    // already clears that on its own, and keeps its wider gutter.
    const gutter = (boxSize - contentSize) / 2,
      margin = Math.max(padding, scale * gutter),
      travel = Math.min(1, Math.max(0, pointer / boxSize)),
      edge = margin + travel * (boxSize - scaled - 2 * margin);

    return ((gutter - edge) / (scale - 1) / contentSize) * 100;
  }

  carouselZoomKeydown(event) {
    if (!this.carouselZoomable) return;

    if (event.key === 'Escape' && this._carouselZoomEnabled) {
      this.endCarouselZoom();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      // Keyboard users get the centre of the image; there is no cursor to
      // read an origin from.
      if (this._carouselZoomEnabled) this.endCarouselZoom();
      else this.startCarouselZoom();
    }
  }

  // The hero is downloaded at display size, so scaling it alone just enlarges
  // pixels; fetch a source sized for the zoomed box and swap it in once it has
  // decoded, leaving the current frame on screen until then.
  loadCarouselZoomImage() {
    const zoomUrl = this.zoomImgUrl;
    if (!zoomUrl || zoomUrl === this._carouselZoomSrc) return;
    if (zoomUrl === this.heroSourceUrl) return;

    const loader = new Image();
    // A CSS mask is fetched anonymously; match it so both share a cache entry
    // and the mask never refetches, which would blank the hero.
    if (this.selectedCarouselImage?.is_designer)
      loader.crossOrigin = 'anonymous';
    loader.onload = () => {
      // The selection can move on while a large source is in flight.
      if (this._carouselZoomEnabled && this.zoomImgUrl === zoomUrl)
        this._carouselZoomSrc = zoomUrl;
    };
    loader.src = zoomUrl;
  }

  // What the hero paints now: the mask source on a designer slide, the <img>
  // src otherwise.
  get heroSourceUrl() {
    const image = this.selectedCarouselImage;
    return image?.is_designer ? image.designer?.imageUrl : this.heroImgUrl;
  }

  // The loaded zoom source, ignored once the selection has moved past it.
  get activeZoomSrc() {
    return this._carouselZoomSrc === this.zoomImgUrl
      ? this._carouselZoomSrc
      : '';
  }

  get zoomImgUrl() {
    const image = this.selectedCarouselImage;
    if (!image) return null;
    // A CSS mask rasterizes from the file it is handed, so the designer slide
    // needs a larger source of its own.
    if (image.is_designer) return image.designer?.zoomUrl || null;
    // Rendered and panel slides arrive at a fixed size with no transform URL.
    if (image.hasOwnProperty('carousel_src')) return null;
    return this.imagebossUrl(image.src, null, CAROUSEL_ZOOM_IMAGE_HEIGHT);
  }

  get carouselHeroImageHTML() {
    const zoomable = this.carouselZoomable;
    return html`
      <div
        class=${classMap({
          'carousel-hero-image': true,
          'zoom-available': zoomable,
          'zoom-enabled': zoomable && this._carouselZoomEnabled,
          // Designer slides are the panel itself; a tint reads as grey resin.
          'bg-tinted':
            !this.isFullPanelImage && !this.selectedCarouselImage?.is_designer,
        })}
        style=${styleMap({
          '--carousel-zoom-origin-x': `${this._carouselZoomOrigin.x}%`,
          '--carousel-zoom-origin-y': `${this._carouselZoomOrigin.y}%`,
        })}
        id="carousel-hero-image"
        role=${zoomable ? 'button' : nothing}
        tabindex=${zoomable ? '0' : nothing}
        aria-pressed=${zoomable ? String(this._carouselZoomEnabled) : nothing}
        aria-label=${zoomable ? 'Zoom image' : nothing}
        @click=${this.toggleCarouselZoom}
        @keydown=${this.carouselZoomKeydown}
        @mousemove=${this.carouselZoomMoved}
        @mouseleave=${this.endCarouselZoom}
      >
        ${this.carouselHeroMediaHTML}
        ${this._fetchingNewCarouselImage || this.designerSlideLoading
          ? html`
              <div class="loading-overlay">
                <img
                  class="loading-spinner"
                  src="/static/images/3form-preloader.gif"
                  alt="Loading..."
                />
              </div>
            `
          : null}
        ${this.viewerFrameHTML} ${this.carouselArrowHTML('previous')}
        ${this.carouselArrowHTML('next')}
      </div>
    `;
  }

  get carouselHeroMediaHTML() {
    const image = this.selectedCarouselImage;
    if (image.is_video) {
      return html`
        <vimeo-video id="hero-media" videoid="${this.videoId}"></vimeo-video>
      `;
    }
    if (image.is_spinshot) {
      return html`
        <iframe
          class="spinshotFrame"
          id="hero-media"
          src="${this.spinshotUrl}"
          frameborder="0"
          scrolling="no"
          style="border: 0; position: absolute; top: 0; left: 0; width: 100%; height:100% ;"
          allowfullscreen=""
        ></iframe>
      `;
    }
    if (image.is_designer) return this.designerHeroHTML;
    if (image.is_viewer_3d) return nothing;

    return html`
      <img
        id="hero-media"
        class="carousel-img"
        src=${this.activeZoomSrc || this.heroImgUrl}
        decoding="async"
        @load=${this.carouselImageLoaded}
      />
    `;
  }

  carouselArrowHTML(direction) {
    return html`
      <span
        @click="${event => {
          event.stopPropagation();
          this.changeCarouselImage(direction, event);
        }}"
        class="${classMap({
          'carousel-arrow': true,
          'left-arrow': direction === 'previous',
          'right-arrow': direction === 'next',
          hidden: this.hideImageSelections,
        })}"
      >
        ${icon('arrow')}
      </span>
    `;
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="block-grid">
          ${this.returnUrl
            ? html`
                <a
                  class="all-patterns-btn print-none"
                  href="${this.returnUrl}"
                  @click="${event =>
                    handleAdobeNavEvent({
                      navString: `NAV|Walter|Back`,
                      destinationUrl: this.returnUrl,
                      event,
                    })}"
                >
                  ${icon('arrow-thin')}
                  <p class="font-size-small">Back</p>
                </a>
              `
            : html``}
          <div
            class=${classMap({
              'carousel-images': true,
              'print-none': true,
              hidden: this.hideImageSelections,
            })}
          >
            <div
              class="carousel-images-sticky"
              style="height: ${this._carouselHeight}px"
            >
              ${this._carouselImagesArray.map(
                (image, index) => html`
                  ${this.carouselImageHTML(image, index)}
                  ${image[0] === 'designer'
                    ? this.viewerPlaceholderThumbHTML
                    : nothing}
                `
              )}
            </div>
          </div>
          <div class="carousel-hero">
            ${this.mobileStickyHeaderHTML}
            <div class="carousel-hero-content-wrapper" id="hero-wrapper">
              <div class="relative-position" id="carousel-hero-container">
                ${this.carouselHeroImageHTML}
              </div>
              <div class="carousel-bottom-row" id="hero-caption">
                <p class="font-size-small carousel-caption">
                  ${unsafeHTML(this.thumbnailDescription)}
                </p>
                ${this.hideDownloadCta
                  ? html``
                  : html`
                      <a
                        href=${this.selectedCarouselImage.full_resolution_src}
                        target="_blank"
                        class=${classMap({
                          'print-none': true,
                          'download-icon': true,
                          'is-busy': this._exporting,
                        })}
                        aria-busy=${this._exporting ? 'true' : 'false'}
                        @click="${event =>
                          this.carouselDownloadIconClicked(
                            this.selectedCarouselImage,
                            event
                          )}"
                      >
                        <span id="download-icon">
                          <p>
                            ${this._exporting
                              ? 'Preparing Image…'
                              : this.downloadCtaLabel}
                          </p>
                          ${icon('download-large')}
                        </span>
                      </a>
                    `}
              </div>
            </div>
          </div>
          <div class="title-header">
            ${this.blockLabel
              ? html`
                  <p class="block-label">${unsafeHTML(this.blockLabel)}</p>
                `
              : html``}
            <h1>${unsafeHTML(this.headerStr)}</h1>
          </div>
          <p class="description">${unsafeHTML(this.descriptionStr)}</p>
          ${this.ctaButtonsHTML}
          ${this.hasColorVariants || this.hasMaterialVariants
            ? html`
                <div class="variants-wrapper print-none">
                  ${this.materialsHTML}
                  ${this.hasColorVariants
                    ? html`
                        <span class="variants-header">
                          ${this.variantsHeaderText}
                        </span>
                        <div class="variants">
                          ${this._variants.map(
                            v => html`
                              <a
                                href="${v.link_path}"
                                class="variant"
                                @click="${event => {
                                  const cleanName = adobeCleanString(
                                    v.name || v.slug
                                  );
                                  handleAdobeNavEvent({
                                    navString: `NAV|Change Variant|${cleanName}`,
                                    destinationUrl: v.link_path,
                                    event,
                                  });
                                }}"
                              >
                                <span>
                                  <img
                                    class=${this.isSelectedVariant(v)
                                      ? 'selected-variant'
                                      : 'not-selected-variant'}
                                    src=${this.variantImgUrl(v)}
                                  />
                                  <p class="variant-name">${v.name}</p>
                                </span>
                              </a>
                            `
                          )}
                        </div>
                      `
                    : html``}
                </div>
              `
            : html``}
          ${this._barrels.length > 0
            ? html`
                <div class="barrels">
                  ${this._barrels.map(this.renderBarrel.bind(this))}
                </div>
              `
            : html``}
          <div class="stupid-spacer"></div>
          ${this._productDetailsBlockData
            ? html`
                <product-details-block
                  content=${JSON.stringify(this._productDetailsBlockData)}
                ></product-details-block>
              `
            : html``}
          ${this._materialDetailsBlockData
            ? html`
                <material-details-block
                  .sampleCta=${this.materialDetailsSampleCta}
                  class=${classMap({
                    'under-product-details': this
                      ._showMaterialDetailsBlockOnProductPage,
                  })}
                  content=${JSON.stringify(this.materialDetailsBlockContent)}
                ></material-details-block>
              `
            : html``}
          ${this.horizontalRule}
          <img
            class="footer-logo print-only"
            src="/static/icons/SVG/3form-logo.svg"
          />
          <span class="footer-info print-only">
            <span>
              <p>800.726.0126</p>
              <p>info@3-form.com</p>
            </span>
            <span>
              <p>2300 South 2300 West</p>
              <p>Salt Lake City, UT 84119</p>
            </span>
            <span>
              <p>3-form.com</p>
            </span>
          </span>
        </div>
      </div>
    `;
  }

  get mobileStickyHeaderHTML() {
    return html`
      <div
        id="sticky-hero-wrapper-mobile"
        class=${classMap({
          'hide-sticky-wrapper': !this._showMobileStickyHeader,
        })}
      >
        <div
          class="carousel-hero-content-wrapper"
          id="hero-wrapper-mobile-walter"
        >
          <div class="carousel-hero-image">
            ${this.stickyHeroMediaHTML}
            ${this._fetchingNewCarouselImage || this.designerSlideLoading
              ? html`
                  <div class="loading-overlay">
                    <img
                      class="loading-spinner"
                      src="/static/images/3form-preloader.gif"
                      alt="Loading..."
                    />
                  </div>
                `
              : null}
            <div
              class=${classMap({
                'carousel-arrow': true,
                'carousel-arrows-sticky': true,
                'print-none': true,
                hidden: this.hideImageSelections,
              })}
            >
              <span
                @click="${() => this.changeCarouselImage('previous', event)}"
                class="left-arrow"
              >
                ${icon('arrow')}
              </span>
              <span
                @click="${() => this.changeCarouselImage('next', event)}"
                class="right-arrow"
              >
                ${icon('arrow')}
              </span>
            </div>
          </div>
        </div>
        <div class="sticky-header-content">
          <h1>${unsafeHTML(this.headerStr)}</h1>
          <p class="carousel-caption">
            ${unsafeHTML(this.thumbnailDescription)}
          </p>
        </div>
      </div>
    `;
  }

  // A designer slide's file is a mask with transparent ink, so an <img> of it
  // paints nothing; the sticky header draws the recolored preview instead.
  get stickyHeroMediaHTML() {
    const image = this.selectedCarouselImage;
    if (image.is_designer) {
      const { designer } = image;
      return html`
        <div
          class="carousel-img-sticky ${previewClassName(designer.selection)}"
          style=${styleMap(this.designerPreviewStyle(designer))}
          role="img"
          aria-label="${designer.patternName} ${designer.variantName}"
        >
          <div class="pattern-preview__ground"></div>
          <div class="pattern-preview__ink"></div>
        </div>
      `;
    }

    return html`
      <img
        id="hero-img-mobile"
        class="carousel-img-sticky"
        src=${this.heroImgUrl}
        decoding="async"
        @load=${this.carouselImageLoaded}
      />
    `;
  }

  carouselImageLoaded() {
    // Ensure that the image is rendered before setting the button height
    if (
      !this.heroImageElement ||
      this.heroImageElement.offsetHeight === 0 ||
      this.isEmbeddedSlide(this.selectedCarouselImage)
    )
      return;

    this._fetchingNewCarouselImage = false;

    if (!!this.heroImageElementWrapper && !!this.heroImageElement) {
      this._carouselHeight = this.heroImageElementWrapper.offsetHeight;
    }
  }

  get primaryCtaClickHandler() {
    let clickHandler;
    if (this.primaryCtaData.isSampleOrderCta) {
      clickHandler = this.openSampleOrderModal;
    } else {
      clickHandler = this.addSelectedProductToCart;
    }
    return clickHandler;
  }

  get ctaButtonsHTML() {
    let renderSecondaryCta = false;
    const secondaryCta = {
      url: '',
      text: '',
      isSampleOrderCta: false,
    };
    let secondaryClickHandler;

    if (this._hasOrderSheetBarrel && this.renderOrderSheetsBarrel) {
      renderSecondaryCta = true;
      secondaryCta.text = 'Order 48"x 96" Panel';
      secondaryClickHandler = this.openOrderSheetDialog;
      secondaryCta.analyticsName = `INP|Open Order Panel|${adobeCleanString(
        this.headerStr
      )}`;
    } else if (this.secondaryCtaData) {
      renderSecondaryCta = true;
      secondaryCta.text = this.secondaryCtaData.text;
      secondaryCta.url = this.secondaryCtaData.url;
      if (this.isSampleOrderCta(this.secondaryCtaData.text)) {
        // openSampleOrderModal reports the click as the same modal-open INP
        // the primary sample CTA sends, so the button stays silent.
        secondaryCta.isSampleOrderCta = true;
        secondaryClickHandler = this.openSampleOrderModal;
      } else if (this.isQuoteCta(this.secondaryCtaData)) {
        // Same path as the barrel's quote CTA, so the form gets the product
        // context and the click reports as NAV.
        secondaryCta.url = secondaryCta.url || '/request-quote';
        secondaryClickHandler = event =>
          this.requestMoreInfoClicked(event, secondaryCta.text);
      } else {
        const cleanHeader = adobeCleanString(secondaryCta.text);
        const cleanCtaText = adobeCleanString(this.headerStr);
        secondaryCta.analyticsName = `INP|${cleanHeader}|${cleanCtaText}`;
      }
    }

    return !this.content.direct_spree_product_id
      ? html`
          <div class="cta-btns">
            ${this.primaryCtaHidden
              ? html`
                  <cta-button
                    cta-data=${this.fallbackCtaData}
                    cta-type="primary"
                    @click="${this.fallbackCtaClickHandler}"
                    .hidden="${this.fallbackCtaHidden ||
                      this.ctaHiddenByBarrel(this.content.fallback_cta)}"
                    .disabled="${this.fallbackCtaDisabled}"
                    class="primary"
                  ></cta-button>
                `
              : html`
                  <cta-button
                    .ctaData=${this.primaryCtaData}
                    cta-type="primary"
                    @click="${this.primaryCtaClickHandler}"
                    .hidden="${this.primaryCtaHidden ||
                      this.ctaHiddenByBarrel(this.content.cta)}"
                    .disabled="${this.primaryCtaDisabled}"
                  ></cta-button>
                `}
            ${renderSecondaryCta
              ? html`
                  <cta-button
                    cta-data=${JSON.stringify(secondaryCta)}
                    cta-type="secondary"
                    @click="${secondaryClickHandler}"
                  ></cta-button>
                `
              : html``}
            ${this.primaryCtaHidden &&
            this.fallbackCtaData?.toLowerCase()?.includes('sample unavailable')
              ? html`
                  <p class="font-size-small">
                    Samples may temporarily be unavailable. However, full panels
                    are still available and are all made to order.
                  </p>
                `
              : html``}
          </div>
        `
      : html`
          <div class="cta-btns">
            <div class="cta-btn-row" .hidden="${this.primaryCtaHidden}">
              <label for="walter-direct-qty" class="visually-hidden">
                Quantity
              </label>
              <input
                type="number"
                id="walter-direct-qty"
                name="quantity"
                min="1"
                step="1"
                @change=${this.directQtyChanged}
                .value="${this._addToCartQuantity}"
              />
              <cta-button
                cta-data=${this.directPrimaryCtaData}
                cta-type="primary"
                @click="${this.addSelectedProductToCart}"
                .disabled="${this.primaryCtaDisabled}"
              ></cta-button>
            </div>
          </div>
        `;
  }

  carouselImageHTML(image, index) {
    if (image[1].is_designer) {
      return this.designerThumbHTML(image[1], index);
    }

    if (!!image[1].src || !!image[1].thumb_src) {
      return html`
        <div
          class="${classMap({
            image: true,
            'spinshot-overlay': image[1].is_spinshot || image[1].is_viewer_3d,
          })}"
        >
          <img
            src=${this.thumbUrl(image[1])}
            data-index=${index}
            @click="${this._updateSelectedCarouselImage}"
            class="${this.isCurrentCarouselImage(image[1])
              ? 'selected-image'
              : 'not-selected-image'}"
          />
          ${this.spinshotOverlayHTML(image)}
        </div>
      `;
    } else {
      return html``;
    }
  }

  spinshotOverlayHTML(image) {
    if (!image[1].is_spinshot && !image[1].is_viewer_3d) return html``;
    return html`
      <img
        src="/static/icons/SVG/360_icon_reversed.svg"
        class="image overlay"
      />
    `;
  }

  thumbUrl(image) {
    if (image.hasOwnProperty('thumb_src')) {
      return image.thumb_src;
    } else {
      return this.imagebossUrl(image.src, null, 100);
    }
  }

  updateCarouselImages() {
    if (!this.hasSelectedCustomPattern || !this.hasSelectedHardwareColor) {
      return;
    }
    this.fetchThreekitRenderedImages();
  }

  fetchThreekitRenderedImages() {
    const perPage = isMobileLayout() ? '24' : '10';
    const searchParams = new URLSearchParams({
      threekit_hardware_finish_id: this._selectedHardwareFinish.id,
      threekit_stage_id: this._selectedCustomPattern.threekit_stage_id,
      render_interlayer_id: this._selectedCustomPattern.render_interlayer_id,
      sample_type_id: this._selectedCustomPattern.sample_type_id,
      perPage: perPage,
    });
    const layersUrl = `/threekit_layers?${searchParams}`;

    if (this._loadedThreekitImages[layersUrl]) {
      this.checkAddMaterialDetailsBlock(this._loadedThreekitImages[layersUrl]);
      return this.addThreekitImages(
        this._loadedThreekitImages[layersUrl].threekit_layers
      );
    }

    const init = {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };

    this._fetchingNewCarouselImage = true;

    fetch(layersUrl, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        if (json && json?.threekit_layers) {
          this._loadedThreekitImages[layersUrl] = json;

          const { panel_image_src } = json;
          if (panel_image_src) {
            // add panel image to the carousel with the same object structure
            // so it receives the same treatment as the other rendered images
            this._loadedThreekitImages[layersUrl].threekit_layers.push({
              thumb_src: panel_image_src,
              carousel_src: panel_image_src,
              src: panel_image_src,
              full_resolution_src: panel_image_src,
              camera_id: this._panelImageId,
            });
          }

          this.addThreekitImages(
            this._loadedThreekitImages[layersUrl].threekit_layers
          );
          this.checkAddMaterialDetailsBlock(json);
        }
      })
      .catch(error => {
        logErrorToSentry(error);
      });

    this.sendAnalyticsVisualizationSelectedEvent();
  }

  sendAnalyticsImageDownloadEvent(image) {
    if (image.is_digital_print) {
      return this.sendDigitalPrintDownloadEvent(image);
    }
    if (image.is_designer || image.is_viewer_3d) {
      return this.sendDesignerDownloadEvent(image);
    }

    let filename = image.filename || '';
    const productName = this.headerStr || '';
    const patternName = this.configuredPatternName || '';
    const hardwareFinishName = this._selectedHardwareFinish?.display_name || '';
    const { image_category, adobeEventStr } = this.analyticsImageType();

    const downloadData = {
      file_format: image.is_designer || image.is_viewer_3d ? 'PNG' : 'JPG',
      file_name: analyticsLabelFromString(filename),
      link_url: image.full_resolution_src,
      link_type: 'IMAGE',
      placement: analyticsLabelFromString(adobeEventStr),
    };

    handleFileDownloadEvent(analyticsCtaDataObject(downloadData));

    // One download, one event: a rendered image is told apart by its source,
    // not by a second file_download.
    pushDataLayerEvent('file_download', {
      download_source:
        image_category === 'rendered' ? 'visualization' : 'carousel',
      visualized_product: productName,
      visualized_pattern: patternName,
      visualized_hardware: hardwareFinishName,
      image_category,
    });
  }

  // Both designer slides report the configuration they were downloaded
  // with, as the Digital Print configurator does, rather than a filename.
  sendDesignerDownloadEvent(image) {
    const { designer } = image;
    handleFileDownloadEvent({
      file_format: 'PNG',
      file_name: designer.analyticsLabel || designer.filename,
      link_url: image.is_viewer_3d
        ? image.full_resolution_src
        : designer.exportUrl,
      link_type: 'IMAGE',
      placement: this.designerDownloadPlacement(image),
    });
  }

  // Labelled with the configuration, like the barrel's own download links.
  sendDigitalPrintDownloadEvent(image) {
    handleFileDownloadEvent({
      file_format: image.is_viewer_3d ? 'PNG' : 'JPG',
      file_name: image.analyticsLabel || image.filename,
      link_url: image.full_resolution_src,
      link_type: 'IMAGE',
      placement: DIGITAL_PRINT_TOOL_NAME,
    });
  }

  designerDownloadPlacement(image) {
    if (image.is_viewer_3d) return 'Graphic Pattern Designer Room View';
    if (image.is_swatch) return 'Graphic Pattern Designer Swatch Image';
    return 'Graphic Pattern Designer Panel Image';
  }

  analyticsImageType() {
    const description =
      this.selectedCarouselImage?.thumbnail_description?.toLowerCase() || '';
    const isRendered = this.selectedCarouselImage?.rendered;

    let image_category = '';
    let label = 'Default';

    if (isRendered) {
      image_category = 'rendered';
      label = 'Render';
    } else if (this.selectedCarouselImage?.is_viewer_3d) {
      image_category = '3d-viewer';
      label = '3D Viewer';
    } else if (description.includes('drag to spin')) {
      image_category = 'imajize';
      label = 'Imajize';
    } else if (description.includes('panel')) {
      image_category = 'full-panel';
      label = 'Full Panel';
    } else if (description.includes('sample')) {
      image_category = 'sample';
      label = 'Sample';
    } else if (description.includes('detail')) {
      image_category = 'detail';
      label = 'Detail';
    }

    return {
      image_category,
      adobeEventStr: `Image ${label}`,
    };
  }

  // Threekit renders belong to the visualizer and the 3D slide to whichever
  // designer feeds it, so looking at one counts as using that tool.
  get selectedImageToolAnalytics() {
    const { image_category } = this.analyticsImageType();
    if (image_category === 'rendered') {
      return createToolAnalytics('Visualization');
    }
    if (image_category === '3d-viewer') {
      return createToolAnalytics(
        this.selectedCarouselImage.is_digital_print
          ? DIGITAL_PRINT_TOOL_NAME
          : 'Graphic Pattern Designer'
      );
    }
    return null;
  }

  sendAnalyticsCarouselImageSelect() {
    const formattedEventStr = analyticsLabelFromString(
      this.analyticsImageType().adobeEventStr
    );
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `INP|Carousel Image Select|${formattedEventStr}`,
      },
    };
    fireAdobeAnalyticsEvent(eventData);
  }

  // Zoom-in only; zooming back out is not a separate interaction.
  sendAnalyticsCarouselImageZoom() {
    const formattedEventStr = analyticsLabelFromString(
      this.analyticsImageType().adobeEventStr
    );
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `INP|Carousel Image Zoom|${formattedEventStr}`,
      },
    };
    fireAdobeAnalyticsEvent(eventData);
  }

  sendAnalyticsVisualizationSelectedEvent() {
    const productName = this.headerStr || '';
    const patternName = this.configuredPatternName || '';
    const hardwareFinishName = this._selectedHardwareFinish?.display_name || '';

    pushDataLayerEvent('visualization_product_selected', {
      visualized_product: productName,
      visualized_pattern: patternName,
      visualized_hardware: hardwareFinishName,
    });
  }

  sendAnalyticsVisualizationAvailable() {
    pushDataLayerEvent('visualization_ready', {
      visualized_product: this.headerStr || '',
    });
  }

  async addVideoToCarousel() {
    if (this.videoId) {
      await fetch(`https://vimeo.com/api/v2/video/${this.videoId}.json`)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
        })
        .then(body => {
          if (body[0]) {
            const videoThumb =
              body[0].thumbnail_medium ||
              body[0].thumbnail_large ||
              body[0].thumbnail_small;
            const carouselObject = [
              'video',
              {
                visualization_swatch: '0',
                thumbnail_description: '',
                thumb_src: videoThumb,
                is_video: true,
                src: this.content.vimeo_video_url,
                full_resolution_src: '',
                display_order: this._carouselImagesArray.length,
              },
            ];
            this._carouselImagesArray.push(carouselObject);
          }
        });
    }
  }

  get videoId() {
    return vimeoIdFrom(this.content.vimeo_video_url);
  }

  async addSpinshotToCarousel() {
    if (this.spinshotId) {
      const carouselObject = [
        'spinshot',
        {
          visualization_swatch: '0',
          thumbnail_description: '8" x 8" sample.\nClick & drag to spin.',
          thumb_src: this.spinshotThumb,
          is_video: false,
          is_spinshot: true,
          src: this.spinshotUrl,
          full_resolution_src: '',
          display_order: 0,
        },
      ];
      this._carouselImagesArray.unshift(carouselObject);
    }
  }

  // imajize has changed to spinshot
  get spinshotId() {
    return this.content.imajize_id || '';
  }

  get spinshotUrl() {
    return `https://embed.spinshot.io/${this.spinshotId}`;
  }

  get spinshotThumb() {
    return `https://fast.spinshot.io/${this.spinshotId}/frames/1/w_150.jpg`;
  }

  addThreekitImages(threekitLayers = [], imagesToEndOfList = false) {
    let { _carouselImagesArray } = this;

    // remove any rendered images that are already in the carousel
    for (let i = 0; i < _carouselImagesArray.length; i++) {
      if (_carouselImagesArray[i][1].rendered) {
        _carouselImagesArray.splice(i, 1);
        i--;
      }
    }

    // Push the rendered images to the front of the _carouselImagesArray, but
    // in the order that they come from the server
    if (threekitLayers.length) {
      threekitLayers = imagesToEndOfList
        ? threekitLayers
        : threekitLayers.slice().reverse();
      threekitLayers.forEach((layer, index) => {
        if (layer.camera_id === 1) {
          this._camera1ImageUrl = layer.carousel_src;
        }
        const formattedLayer = [];
        // display order will be sent by the server at some point
        layer.display_order = _carouselImagesArray.length + index;
        layer.rendered = true;
        layer.thumbnail_description = this.renderedImageThumbnailDescription(
          layer
        );
        formattedLayer.push(layer.display_order.toString());
        formattedLayer.push(layer);
        if (imagesToEndOfList) {
          this._carouselImagesArray.push(formattedLayer);
        } else {
          this._carouselImagesArray.unshift(formattedLayer);
        }
      });

      // set the selected image to the first rendered image in the list
      this._selectedCarouselImageId = this._carouselImagesArray.findIndex(
        img => img[1].rendered
      );
    }

    this._fetchingNewCarouselImage = false;
    this.requestUpdate();
  }

  renderedImageThumbnailDescription(layer) {
    let existingDescription = layer.thumbnail_description;
    const useElementsFormat =
        this.blockLabel.toLowerCase().includes('elements') ||
        isDealerPage() ||
        this.content.elements === '1',
      materialType = this._selectedCustomPattern
        ? this._selectedCustomPattern?.sample_type_display_name
        : this.materialType,
      material = `Material | ${materialType}`,
      pattern = this._selectedCustomPattern
        ? this._selectedCustomPattern.hex_code
          ? `Color | ${this.configuredPatternName}`
          : `Pattern | ${this.configuredPatternName}`
        : '',
      hardwarePrefix = useElementsFormat ? '' : 'Hardware | ',
      hardware =
        !!this.headerStr && !layer.productForMaterial
          ? `${hardwarePrefix}${this.blockLabel} ${this.headerStr}`
          : '',
      hardwareFinish =
        this._selectedHardwareFinish?.display_name &&
        this._selectedHardwareFinish?.display_name !== 'No Hardware Finish'
          ? `Hardware Finish | ${this._selectedHardwareFinish?.display_name}`
          : '';

    const defaultDisplayOrder = [
        existingDescription,
        hardware,
        hardwareFinish,
        pattern,
        material,
      ],
      elementsDisplayOrder = [
        hardware,
        existingDescription,
        hardwareFinish,
        pattern,
        material,
      ];

    let displayOrder = useElementsFormat
      ? elementsDisplayOrder
      : defaultDisplayOrder;

    if (layer.camera_id === this._panelImageId) {
      // description including 'panel' is needed for analytics
      existingDescription = `4' x 8' panel`;
      displayOrder = [existingDescription, pattern, material];
    }

    const existingDescriptionIndex = displayOrder.indexOf(existingDescription);

    // clean up empty data points and prevent duplicates
    displayOrder = displayOrder.map((dataPoint, index) => {
      if (!!existingDescription && index === existingDescriptionIndex) {
        return dataPoint;
      } else if (!dataPoint || existingDescription?.includes(dataPoint)) {
        return '';
      }
      return dataPoint;
    });

    return `${displayOrder.filter(Boolean).join('\n')}`;
  }

  get materialType() {
    let materialType = '';
    if (
      typeof this.content.sample_types === 'object' &&
      Object.keys(this.content.sample_types).length
    ) {
      materialType = Object.keys(this.content.sample_types)[0];
    } else if (this.content.sample_type_slug) {
      materialType = this.content.sample_type_slug;
    } else if (
      this.content.interlayer_variant_slug.includes('hush') ||
      this.content.interlayer_slug.includes('acoustic')
    ) {
      materialType = 'sola-felt';
    }
    materialType = materialType
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
    return materialType;
  }

  get configuredPatternName() {
    if (!this._selectedCustomPattern) return '';
    // sometimes only one or the other is available
    const { parentName, name } = this._selectedCustomPattern;
    return `${parentName || ''} ${name || ''}`.trim();
  }

  get hasSelectedHardwareColor() {
    return Object.keys(this._selectedHardwareFinish).length > 0;
  }

  get hasSelectedCustomPattern() {
    return (
      !!this._selectedCustomPattern &&
      typeof this._selectedCustomPattern === 'object' &&
      Object.keys(this._selectedCustomPattern).length > 0
    );
  }

  get heroImageElement() {
    return this.shadowRoot.getElementById('hero-media');
  }

  get heroImageElementWrapper() {
    return this.shadowRoot.getElementById('hero-wrapper');
  }

  get heroImageBox() {
    return this.shadowRoot.getElementById('carousel-hero-image');
  }

  isCurrentCarouselImage(image) {
    return image.src === this.selectedCarouselImage.src;
  }

  barrelHeaderIcon(isExpanded) {
    return isExpanded ? 'minus' : 'plus';
  }

  _updateSelectedCarouselImage(event) {
    const index = event.target.getAttribute('data-index');
    this._selectedCarouselImageId = parseInt(index);
    this.sendAnalyticsCarouselImageSelect();
    this.selectedImageToolAnalytics?.view(
      this.analyticsImageType().adobeEventStr
    );
  }

  changeCarouselImage(direction, event = null) {
    const imagesCount = this._carouselImagesArray.length;
    const currentId = this._selectedCarouselImageId;
    if (direction === 'previous') {
      currentId === 0
        ? (this._selectedCarouselImageId = imagesCount - 1)
        : this._selectedCarouselImageId--;
    } else if (direction === 'next') {
      currentId === imagesCount - 1
        ? (this._selectedCarouselImageId = 0)
        : this._selectedCarouselImageId++;
    }
    if (
      this._showMobileStickyHeader &&
      this.isEmbeddedSlide(this.selectedCarouselImage)
    ) {
      // skip videos in the sticky mobile header
      this.changeCarouselImage('next');
    }
    if (!!event) {
      handleAdobeNavEvent({
        navString: `INP|Hero Carousel Arrow|${direction}`,
        event,
      });
    }
  }

  addVariants() {
    if (!this.variantsUrl) return;

    const init = { method: 'GET', headers: { Accept: 'text/html' } };
    fetch(this.variantsUrl, init)
      .then(response => {
        if (response.ok) {
          return response.json();
        }
      })
      .then(json => {
        this.handleVariantsResponse(json);
      })
      .catch(error => {
        logErrorToSentry(error);
      });
  }

  async setupSpreeData() {
    this.cart = await fetchCart();
    if (this.cart) updateCartIndicator(this.cart.data.attributes.item_count);

    if (!this.productId) {
      this.primaryCtaHidden = true;
      return;
    }

    this.includedPropertiesById = {};
    const productAvailabilityResponse = await checkProductAvailability(
      this.productId
    );

    if (productAvailabilityResponse.isFail()) {
      // Should be hidden and disabled
      this.primaryCtaHidden = true;
    } else {
      // Should be shown and enabled
      this.primaryCtaDisabled = false;

      this.includedPropertiesById[
        this.productId
      ] = productAvailabilityResponse.success().included;
    }
  }

  // Once a page offers a colorway in a second material, the colors row lists
  // only the current material and the Material row switches between twins.
  handleVariantsResponse(json) {
    const variants = Array.isArray(json) ? json : [];
    const current = variants.find(v => this.isSelectedVariant(v));
    const multiMaterial = variants.some(
      v => v.base_slug && v.base_slug !== v.slug
    );

    this._currentMaterialName = (multiMaterial && current?.material_name) || '';
    this._materialVariants = current
      ? this.materialTwins(variants, current)
      : [];
    this._variants =
      multiMaterial && current
        ? variants.filter(v => v.sample_type_slug === current.sample_type_slug)
        : variants;

    if (this._materialVariants.length && this.materialDetailsHashTargeted) {
      this.updateComplete.then(() => this.scrollToMaterialDetails());
    }
  }

  // The current colorway in every material, primary first.
  materialTwins(variants, current) {
    const baseSlug = current.base_slug || current.slug;
    const twins = variants.filter(v => (v.base_slug || v.slug) === baseSlug);
    if (twins.length < 2) return [];

    return [
      ...twins.filter(v => v.slug === baseSlug),
      ...twins.filter(v => v.slug !== baseSlug),
    ];
  }

  get hasColorVariants() {
    return !!this._variants && this._variants.length > 1;
  }

  get hasMaterialVariants() {
    return this._materialVariants.length > 1;
  }

  get materialsHTML() {
    if (!this.hasMaterialVariants) return html``;

    return html`
      <div class="materials">
        <span class="variants-header">Material</span>
        <div class="variants">
          ${this._materialVariants.map(
            v => html`
              <a
                href="${v.link_path}"
                class="variant"
                aria-current=${this.isSelectedVariant(v) ? 'page' : nothing}
                @click="${event => {
                  handleAdobeNavEvent({
                    navString: `NAV|Change Material|${adobeCleanString(
                      v.material_name
                    )}`,
                    destinationUrl: v.link_path,
                    event,
                  });
                }}"
              >
                <span>
                  <img
                    class=${this.isSelectedVariant(v)
                      ? 'selected-variant'
                      : 'not-selected-variant'}
                    src=${this.variantImgUrl(v)}
                    alt=""
                  />
                  <p class="variant-name">${v.material_name}</p>
                </span>
              </a>
            `
          )}
        </div>
      </div>
    `;
  }

  // Tabs reload the page on the same section, so they carry its fragment.
  get materialDetailsBlockContent() {
    if (!this.hasMaterialVariants) return this._materialDetailsBlockData;

    return {
      ...this._materialDetailsBlockData,
      materialTabs: this._materialVariants.map(v => ({
        label: v.material_name,
        url: `${v.link_path}#${MATERIAL_DETAILS_HASH}`,
        selected: this.isSelectedVariant(v),
      })),
    };
  }

  get materialDetailsHashTargeted() {
    return window.location.hash === `#${MATERIAL_DETAILS_HASH}`;
  }

  // The block sits two shadow roots deep, where a URL fragment can't reach.
  async scrollToMaterialDetails() {
    const block = this.renderRoot.querySelector('material-details-block');
    if (!block) return;

    await block.updateComplete;
    window.scrollTo({
      top:
        block.getBoundingClientRect().top +
        window.pageYOffset -
        this.stickyHeaderOffset,
    });
  }

  isSelectedVariant(variant) {
    return this.interlayerVariantSlug === variant.slug;
  }

  variantImgUrl(variant) {
    // Image ratio for other images is 25/32
    // this adjusts the color chip images to cover that ratio from ImageBoss
    const height = variant.slug === 'color-chip' ? (100 / 25) * 32 : null;
    return this.imagebossUrl(variant.thumbnail_src, 100, height);
  }

  openSampleOrderModal() {
    useAddToCartModal();
    const setupAddToCartDataEvent = new CustomEvent('setupAddToCartData', {
      detail: {
        analyticsProductId: this.productId,
        productVariantId: this.productVariantId,
        headerStr: this.headerStr,
        imageUrl: this.selectedSampleTypeImage,
        productSize: this.productSize,
        sampleType: this.selectedSampleTypeKey,
        gaugeStr: this.selectedSampleTypeProductGauge,
      },
      bubbles: true,
      composed: true,
    });
    const el = document.getElementById('modal-request-sample');
    el.dispatchEvent(setupAddToCartDataEvent);
    setTimeout(() => {
      // Wrapping setTimeout to avoid race condition
      this.showMicroModal();
    }, 0);

    // fire adobe event
    const eventData = {
      type: 'click',
      detail: {
        eVar8: `INP|Sample Order Modal Open|${this.headerStr}`,
      },
    };
    fireAdobeAnalyticsEvent(eventData);
  }

  showMicroModal() {
    MicroModal.show('modal-request-sample');
  }

  async addSelectedProductToCart() {
    if (this.primaryCtaDisabled) return;
    useCartConfirmationModal();

    this.primaryCtaDisabled = true;

    this.cart = await addToCart({
      variantId: this.productVariantId,
      quantity: this._addToCartQuantity,
      analyticsProductId: this.productId,
    });
    if (this.cart) {
      updateCartIndicator(this.cart.item_count);

      const productSizeValue = this.productSize?.attributes?.value || '';

      changeProductSizeInfoInModal(
        productSizeValue,
        this.selectedSampleTypeDisplayValue
      );

      changeProductNameInModal(
        this.content.header.replace(` ${this.content.name}`, ''),
        this.content.name
      );
      changeProductImageInModal(this.selectedSampleTypeImage);
    }

    this.primaryCtaDisabled = false;
  }

  get productSize() {
    return this.includedPropertiesById
      ? find(this.includedPropertiesById[this.productId], {
          attributes: { name: 'size' },
        })
      : {};
  }

  carouselDownloadIconClicked(file, event) {
    if (file.is_designer && event) {
      // The configured panel only exists in the browser; render it on demand
      // rather than following the raw pattern PNG link.
      event.preventDefault();
      this.downloadDesignerImage(file.designer);
    } else if (file.is_viewer_3d && event) {
      event.preventDefault();
      this.downloadViewerImage(file);
    }
    this.sendAnalyticsImageDownloadEvent(file);
  }

  // ---------------------------------------------------------------------
  // Graphic pattern designer slides. The barrel dispatches its selection and
  // the carousel keeps recolored slides for it at the front, in this order:
  // swatch, full panel, 3D view.
  // ---------------------------------------------------------------------

  updateDesignerSlide(designer) {
    const slide = [
      'designer',
      {
        is_designer: true,
        designer,
        filename: designer.filename,
        src: designer.thumbUrl,
        thumb_src: designer.thumbUrl,
        full_resolution_src: designer.fullResolutionUrl,
        thumbnail_description: `${designer.patternName} ${designer.variantName} Panel`,
      },
    ];

    this.updateSwatchSlide(designer.swatch);

    const index = this.slideIndex('designer');
    if (index === -1) {
      this.insertSlideAt(this.slideIndex('swatch') + 1, slide);
    } else {
      this._carouselImagesArray[index] = slide;
    }

    // A user on one of the barrel's slides stays there; anyone else is
    // brought to the first of them.
    const selected = this.selectedCarouselImage;
    if (!selected?.is_designer && !selected?.is_viewer_3d) {
      this._selectedCarouselImageId = Math.max(0, this.slideIndex('swatch'));
    }

    this._designerLabel = designer.variantName;
    this._designerColor = designer.selection.foreground;
    this._designerSlideNeedsMeasure = true;
    this.preloadMaskImage(designer.imageUrl);
    if (this._carouselZoomEnabled) this.loadCarouselZoomImage();
    this.updateViewerSlide(designer);
    this.requestUpdate();
  }

  slideIndex(key) {
    return this._carouselImagesArray.findIndex(image => image[0] === key);
  }

  // An empty carousel has no selected slide to keep, so the first one
  // inserted becomes the selection.
  insertSlideAt(at, slide) {
    const keep = this._carouselImagesArray.length > 0;
    this._carouselImagesArray.splice(at, 0, slide);
    if (keep && this._selectedCarouselImageId >= at) {
      this._selectedCarouselImageId++;
    }
  }

  // The selected variant's square swatch, recolored like the panel: the
  // first slide, like the sample photo on a material page.
  updateSwatchSlide(swatch) {
    const index = this.slideIndex('swatch');

    if (!swatch) {
      if (index !== -1) this.removeCarouselImageAt(index);
      return;
    }

    const slide = [
      'swatch',
      {
        is_designer: true,
        is_swatch: true,
        designer: swatch,
        filename: swatch.filename,
        src: swatch.thumbUrl,
        thumb_src: swatch.thumbUrl,
        full_resolution_src: swatch.fullResolutionUrl,
        thumbnail_description: `${swatch.patternName} ${swatch.variantName} Swatch`,
      },
    ];

    if (index === -1) {
      this.insertSlideAt(0, slide);
    } else {
      this._carouselImagesArray[index] = slide;
    }

    this.preloadMaskImage(swatch.imageUrl);
  }

  // A new variant means a new mask image and a blank hero until it lands,
  // so the carousel's loading overlay covers the wait. Color changes reuse
  // the image and never wait. Fetched with CORS like the CSS mask so the
  // cache entry is shared.
  preloadMaskImage(url) {
    if (!url || this._loadedMaskUrls.has(url) || this._loadingMaskUrls.has(url))
      return;
    this._loadingMaskUrls.add(url);
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';
    const done = () => {
      this._loadingMaskUrls.delete(url);
      this._loadedMaskUrls.add(url);
      this.requestUpdate();
    };
    image.onload = done;
    image.onerror = done;
    image.src = url;
  }

  get designerSlideLoading() {
    const image = this.selectedCarouselImage;
    return (
      !!image?.is_designer &&
      this._loadingMaskUrls.has(image.designer?.imageUrl)
    );
  }

  designerPreviewStyle(designer, imageUrl = designer.imageUrl) {
    return {
      ...previewStyle(imageUrl, designer.selection),
      '--designer-aspect': `${designer.width || 1} / ${designer.height || 2}`,
    };
  }

  get designerHeroHTML() {
    const { designer } = this.selectedCarouselImage;
    return html`
      <div
        id="hero-media"
        class="carousel-img ${previewClassName(designer.selection)}"
        style=${styleMap(
          this.designerPreviewStyle(
            designer,
            this.activeZoomSrc || designer.imageUrl
          )
        )}
        role="img"
        aria-label="${designer.patternName} ${designer.variantName}"
        @contextmenu=${this.designerHeroContextMenu}
      >
        <div class="pattern-preview__ground"></div>
        <div class="pattern-preview__ink"></div>
      </div>
    `;
  }

  // A CSS mask gives the browser no image to save, leaving only "Save Page
  // As", so right-click runs the download button instead.
  designerHeroContextMenu(event) {
    event.preventDefault();
    this.carouselDownloadIconClicked(this.selectedCarouselImage, event);
  }

  designerThumbHTML(image, index) {
    const { designer } = image;
    return html`
      <div class="image">
        <div
          class="${previewClassName(
            designer.selection
          )} ${this.isCurrentCarouselImage(image)
            ? 'selected-image'
            : 'not-selected-image'}"
          style=${styleMap({
            ...this.designerPreviewStyle(designer),
            '--pattern-image': `url("${designer.thumbUrl}")`,
          })}
          data-index=${index}
          @click="${this._updateSelectedCarouselImage}"
        >
          <div class="pattern-preview__ground"></div>
          <div class="pattern-preview__ink"></div>
        </div>
      </div>
    `;
  }

  // The barrel's own download link: the panel slide's export and analytics,
  // whichever slide is showing.
  async downloadConfiguredImageForBarrel(event) {
    if (this._digitalPrint) return this.downloadDigitalPrintCapture(event);
    const slide = this._carouselImagesArray[this.slideIndex('designer')];
    try {
      if (slide) {
        await this.downloadDesignerImage(slide[1].designer);
        this.sendAnalyticsImageDownloadEvent(slide[1]);
      }
    } finally {
      event.detail?.done?.();
    }
  }

  // The "Download Full Resolution Image" link on the designer slide renders
  // the configured panel on demand; other slides follow their real file.
  async downloadDesignerImage(designer) {
    if (this._exporting) return;
    this._exporting = true;
    try {
      const blob = await exportPng(designer.exportUrl, designer.selection);
      downloadBlob(blob, designer.filename);
    } catch (error) {
      logErrorToSentry(error);
    } finally {
      this._exporting = false;
    }
  }

  // ---------------------------------------------------------------------
  // 3D viewer slide. Bitreel's viewer runs in an iframe that follows the
  // designer selection. It loads once the page is idle and stays mounted, so
  // the slide is instant when chosen and keeps its camera between visits.
  // ---------------------------------------------------------------------

  // Slides that are not an <img>: never in the mobile sticky header, never
  // measured on load.
  isEmbeddedSlide(image) {
    return (
      !!image && !!(image.is_video || image.is_spinshot || image.is_viewer_3d)
    );
  }

  get viewerContent() {
    const barrel = (this.content.barrels || []).find(designerContentFrom);
    return designerContentFrom(barrel)?.viewer || null;
  }

  updateViewerSlide(designer) {
    const viewer = this.viewerContent;
    const patternImage = viewer ? viewerPatternImageFrom(designer) : null;
    this._viewerDesigner = designer;

    if (patternImage) {
      this.ensureViewer(viewer.url);
      this._viewer.update({
        config: viewerConfigFrom(designer),
        patternImage,
      });
    }

    this.syncViewerSlide();
  }

  // The slide exists only while the viewer can show this pattern: a library
  // full panel to send, and a viewer build that accepts pattern images (an
  // older build would show its own sample pattern for every page).
  syncViewerSlide() {
    const designer = this._viewerDesigner;
    const viewer = this.viewerContent;
    const patternImage =
      designer && viewer ? viewerPatternImageFrom(designer) : null;
    const supported =
      !!patternImage &&
      this._viewer?.status === 'ready' &&
      this._viewer.supports(VIEWER_CAPABILITIES.patternImage);
    const index = this._carouselImagesArray.findIndex(
      image => image[1].is_viewer_3d
    );

    if (!supported) {
      if (index !== -1) this.removeCarouselImageAt(index);
      return;
    }

    const slide = [
      'viewer-3d',
      {
        is_viewer_3d: true,
        designer,
        filename: designer.filename.replace(/\.png$/, '-3d.png'),
        src: VIEWER_THUMB_SRC,
        thumb_src: VIEWER_THUMB_SRC,
        carousel_src: VIEWER_THUMB_SRC,
        full_resolution_src: viewer.url,
        thumbnail_description: '3D view.\nClick & drag to rotate.',
      },
    ];

    if (index === -1) {
      // Right after the full panel.
      this.insertSlideAt(this.slideIndex('designer') + 1, slide);
    } else {
      this._carouselImagesArray[index] = slide;
    }
  }

  removeCarouselImageAt(index) {
    this._carouselImagesArray.splice(index, 1);
    if (this._selectedCarouselImageId >= index) {
      this._selectedCarouselImageId = Math.max(
        0,
        this._selectedCarouselImageId - 1
      );
    }
  }

  ensureViewer(url) {
    if (this._viewer) return;
    this._viewer = new BitreelViewerClient({ url });
    this._viewer.onViewChanged(detail =>
      createToolAnalytics('Graphic Pattern Designer').viewChanged(detail)
    );
    this._viewer.onChange(viewer => {
      this._viewerStatus = viewer.status;
      this._viewerLoading = viewer.patternImageLoading;
      if (viewer.status === 'ready') this._viewerFailed = false;
      if (viewer.lastError) {
        logErrorToSentry(new Error(viewer.lastError.message));
        viewer.lastError = null;
      }
      this.syncViewerSlide();
      this.requestUpdate();
    });
    this.scheduleViewerLoad();
  }

  // The viewer is about 4 MB, so it starts on the first idle moment rather
  // than with the page. Not on the load event: a hung third-party script
  // would hold the slide back with it. The slide only appears once the
  // viewer is ready, so opening it is always instant.
  scheduleViewerLoad() {
    const allow = () => this.allowViewerLoad();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(allow, { timeout: 3000 });
    } else {
      window.setTimeout(allow, 1500);
    }
  }

  allowViewerLoad() {
    if (this._viewerLoadAllowed) return;
    this._viewerLoadAllowed = true;
    this.requestUpdate();
  }

  // Lit owns the element; the client sets its src so the message listener
  // is in place before the viewer can post ready.
  attachViewerFrame() {
    if (!this._viewer) return;
    const frame = this.shadowRoot.getElementById('viewer-3d-frame');
    if (frame && !this._viewer.attached) {
      this._viewer.attach(frame);
      this.startViewerReadyTimer();
    }
  }

  // A blocked or broken viewer never posts ready and nothing else reports
  // it, so the placeholder says so and Sentry gets the browser details.
  startViewerReadyTimer() {
    window.clearTimeout(this._viewerReadyTimer);
    this._viewerReadyTimer = window.setTimeout(() => {
      if (!this._viewer || this._viewer.status === 'ready') return;
      // Background tabs throttle the frame; wait until the page is seen.
      if (document.hidden) {
        this.startViewerReadyTimer();
        return;
      }
      this._viewerFailed = true;
      logErrorToSentry(
        new Error(
          `3D viewer did not load within ${VIEWER_READY_TIMEOUT_MS / 1000}s`
        )
      );
    }, VIEWER_READY_TIMEOUT_MS);
  }

  // Stands in for the 3D slide while the viewer is on for this user but
  // cannot show the selection, so the option never just disappears.
  get viewerPlaceholderState() {
    if (this._digitalPrint) {
      if (!this.digitalPrintViewerUrl || this.slideIndex('viewer-3d') !== -1) {
        return null;
      }
      return this._viewerFailed ? 'failed' : 'loading';
    }
    if (!this.viewerContent || !this._viewerDesigner) return null;
    if (this._carouselImagesArray.some(image => image[1].is_viewer_3d)) {
      return null;
    }
    // No full panel to send, or a viewer build that cannot take one.
    if (
      !viewerPatternImageFrom(this._viewerDesigner) ||
      this._viewerStatus === 'ready'
    ) {
      return 'unavailable';
    }
    return this._viewerFailed ? 'failed' : 'loading';
  }

  get viewerPlaceholderThumbHTML() {
    const state = this.viewerPlaceholderState;
    if (!state) return nothing;
    const label = {
      unavailable: '3D view not available for this style',
      loading: 'Loading 3D view…',
      failed: '3D view couldn’t load',
    }[state];
    return html`
      <div class="image viewer-placeholder" aria-disabled="true">
        <span class="spinshot-overlay">
          <img src=${VIEWER_THUMB_SRC} alt="" class="not-selected-image" />
          <img
            src="/static/icons/SVG/360_icon_reversed.svg"
            alt=""
            class="image overlay"
          />
        </span>
        <p class="viewer-placeholder-label" role="status">${label}</p>
      </div>
    `;
  }

  get viewerFrameHTML() {
    if (!this._viewer || !this._viewerLoadAllowed) return nothing;
    const active = !!this.selectedCarouselImage?.is_viewer_3d;
    return html`
      <div
        class=${classMap({
          'viewer-3d': true,
          'is-active': active,
          'is-shielded': this._pointerDragging,
        })}
        id=${active ? 'hero-media' : 'viewer-3d'}
      >
        <iframe
          id="viewer-3d-frame"
          title="3D panel viewer"
          allow="fullscreen"
          allowfullscreen
        ></iframe>
        ${this._viewerStatus === 'ready' && !this._viewerLoading
          ? nothing
          : html`
              <div
                class=${classMap({
                  'loading-overlay': true,
                  'is-initial': this._viewerStatus !== 'ready',
                })}
              >
                <img
                  class="loading-spinner"
                  src="/static/images/3form-preloader.gif"
                  alt="Loading..."
                />
              </div>
            `}
      </div>
    `;
  }

  // ---------------------------------------------------------------------
  // Digital print designer slides. The barrel sends its configuration and
  // the carousel shows it on the 3D slide, the default once the viewer is
  // ready, then the image as chosen. The frame, its loading overlay and its
  // ready timer are the 3D slide code above.
  // ---------------------------------------------------------------------

  get digitalPrintContent() {
    const barrel = (this.content.barrels || []).find(digitalPrintContentFrom);
    return digitalPrintContentFrom(barrel);
  }

  get digitalPrintViewerUrl() {
    return this.digitalPrintContent?.embeds?.viewer_url || null;
  }

  // Placement streams every frame while the visitor drags, and only the
  // viewer needs it, so the carousel only re-renders for other changes.
  updateDigitalPrintDesigner(detail) {
    const firstUpdate = !this._digitalPrint;
    const changed = detail.changed || [];
    this._digitalPrint = detail;

    this.sendToDigitalPrintViewer(detail, firstUpdate);
    if (!firstUpdate && changed.every(field => field === 'placement')) return;

    if (firstUpdate || changed.includes('image')) {
      this.updateDigitalPrintImageSlide(detail);
    }
    this.syncDigitalPrintViewerSlide();
    this.requestUpdate();
  }

  // The first update sends the whole configuration; after that only what
  // changed, so an unchanged image or upload isn't sent again.
  sendToDigitalPrintViewer({ config, changed = [] }, everything) {
    const url = this.digitalPrintViewerUrl;
    if (!url) return;
    this.ensureDigitalPrintViewer(url);

    const fields = everything ? Object.keys(config) : changed;
    const partial = {};
    fields.forEach(field => {
      if (field in config) partial[field] = config[field];
    });
    if (partial.image) this._viewerLoading = true;
    this._viewer.setConfig(partial);
  }

  ensureDigitalPrintViewer(url) {
    if (this._viewer) return;
    const toolAnalytics = createToolAnalytics(DIGITAL_PRINT_TOOL_NAME);
    this._viewer = new BitreelFrameClient({
      url,
      prefix: DIGITAL_PRINT_VIEWER_PREFIX,
    });
    this._viewer.on('view-changed', detail =>
      toolAnalytics.viewChanged(detail)
    );
    this._viewer.on('config-changed', () => {
      this._viewerLoading = false;
    });
    this._viewer.onChange(viewer => {
      this._viewerStatus = viewer.status;
      if (viewer.status === 'ready') this._viewerFailed = false;
      if (viewer.lastError) {
        // A broken image leaves the previous one on the panel.
        this._viewerLoading = false;
        logErrorToSentry(
          new Error(viewer.lastError.message || viewer.lastError.code)
        );
        viewer.lastError = null;
      }
      this.syncDigitalPrintViewerSlide();
      this.requestUpdate();
    });
    this.scheduleViewerLoad();
  }

  // Library images come through PhotoShelter's own resizing; a customer's
  // file shows from the page's own copy and has nothing to download back.
  updateDigitalPrintImageSlide({ image, imageTitle, analyticsLabel }) {
    const index = this.slideIndex('digital-print-image');
    if (!image) {
      if (index !== -1) this.removeCarouselImageAt(index);
      return;
    }

    const upload = image.kind === 'upload';
    const slide = [
      'digital-print-image',
      {
        is_digital_print: true,
        analyticsLabel,
        filename: upload
          ? image.name
          : `${digitalPrintFilename(image.imageNumber, 'source')}.jpg`,
        src: upload ? image.objectUrl : image.src,
        thumb_src: upload
          ? image.objectUrl
          : urlAtFit(image.src, DIGITAL_PRINT_THUMB_FIT),
        carousel_src: upload
          ? image.objectUrl
          : urlAtFit(image.src, DIGITAL_PRINT_HERO_FIT),
        full_resolution_src: upload ? '' : urlAtFit(image.src, EMBED_IMAGE_FIT),
        thumbnail_description: imageTitle,
      },
    ];

    if (index !== -1) {
      this._carouselImagesArray[index] = slide;
      return;
    }
    this.insertSlideAt(this.slideIndex('viewer-3d') + 1, slide);
    // Until the 3D view is ready, the image is what the visitor sees first.
    if (this.slideIndex('viewer-3d') === -1) {
      this._selectedCarouselImageId = this.slideIndex('digital-print-image');
    }
  }

  // The slide exists only once the viewer is ready, so choosing it is
  // always instant; the placeholder thumb stands in until then.
  syncDigitalPrintViewerSlide() {
    const detail = this._digitalPrint;
    const index = this.slideIndex('viewer-3d');
    if (!detail || this._viewer?.status !== 'ready') {
      if (index !== -1) this.removeCarouselImageAt(index);
      return;
    }

    const imageSlide = this._carouselImagesArray[
      this.slideIndex('digital-print-image')
    ];
    const thumb = imageSlide?.[1].thumb_src || VIEWER_THUMB_SRC;
    const slide = [
      'viewer-3d',
      {
        is_viewer_3d: true,
        is_digital_print: true,
        analyticsLabel: detail.analyticsLabel,
        filename: `${digitalPrintFilename(detail.lead?.imageNumber, '3d')}.png`,
        src: thumb,
        thumb_src: thumb,
        carousel_src: thumb,
        full_resolution_src: this._viewer.url,
        thumbnail_description: '3D view.\nClick & drag to rotate.',
      },
    ];

    if (index !== -1) {
      this._carouselImagesArray[index] = slide;
      return;
    }
    // The default slide: a visitor still on the image moves to it.
    const onImage = !!this.selectedCarouselImage?.is_digital_print;
    this.insertSlideAt(0, slide);
    if (onImage) this._selectedCarouselImageId = 0;
  }

  async downloadDigitalPrintCapture(event) {
    const slide = this._carouselImagesArray[this.slideIndex('viewer-3d')];
    try {
      if (slide) {
        await this.downloadViewerImage(slide[1]);
        this.sendAnalyticsImageDownloadEvent(slide[1]);
      }
    } finally {
      event.detail?.done?.();
    }
  }

  // Viewers from 1.1.0 render a sized capture themselves, hidden or not.
  // Older ones copy the frame after repainting it on white, and browsers stop
  // painting a hidden or offscreen frame, so the 3D slide is brought into
  // view first. JPEG keeps it small enough for session storage.
  async captureDigitalPrintForQuote() {
    const viewer = this._viewer;
    if (viewer?.status !== 'ready') return null;
    if (!viewer.supports(VIEWER_CAPABILITIES.capture)) return null;

    const [major = 0, minor = 0] = (viewer.version || '')
      .split('.')
      .map(Number);
    if (major < 1 || (major === 1 && minor < 1)) {
      const index = this.slideIndex('viewer-3d');
      if (index !== -1) this._selectedCarouselImageId = index;
      await this.updateComplete;
      const frame = this.shadowRoot.getElementById('viewer-3d-frame');
      const bounds = frame?.getBoundingClientRect();
      if (bounds && (bounds.bottom < 0 || bounds.top > window.innerHeight)) {
        frame.scrollIntoView({ block: 'center' });
      }
    }

    let timer = 0;
    const timeout = new Promise((resolve, reject) => {
      timer = window.setTimeout(
        () => reject(new Error('Digital print quote capture timed out')),
        DIGITAL_PRINT_QUOTE_CAPTURE_TIMEOUT_MS
      );
    });
    try {
      return await Promise.race([
        viewer.capture({
          format: 'jpeg',
          background: 'white',
          scale: 1,
          ...DIGITAL_PRINT_QUOTE_CAPTURE_SIZE,
        }),
        timeout,
      ]);
    } catch (error) {
      logErrorToSentry(error);
      return null;
    } finally {
      window.clearTimeout(timer);
    }
  }

  // Request a Quote from the digital print designer: the quote form gets the
  // design as text and a capture of the 3D view, and the lead gets permanent
  // links to that capture and to a customer's own file.
  async requestQuoteForDigitalPrint(event) {
    const { lead, analyticsLabel, url, digitalPrint, done } = event.detail;
    if (this._quoting) {
      done?.();
      return;
    }
    this._quoting = true;

    try {
      const configurationUrl = window.location.href;
      const upload =
        digitalPrint.image?.kind === 'upload' ? digitalPrint.image : null;
      const capture = await this.captureDigitalPrintForQuote();
      const [configuredImageUrl, customerSuppliedImageUrl] = await Promise.all([
        capture &&
          storeDesignFile(
            capture,
            `${digitalPrintFilename(lead.imageNumber, '3d')}.jpg`
          ),
        upload && storeDesignFile(upload.blob, upload.name),
      ]);

      let imgObj = null;
      try {
        imgObj = capture ? await dataUrlFrom(capture) : null;
      } catch (error) {
        logErrorToSentry(error);
      }

      const imageName = upload ? lead.customerImageName : lead.imageName;
      const description = [
        lead.imageNumber ? `${imageName} (${lead.imageNumber})` : imageName,
        `${lead.material}, Opacity ${lead.opacity}`,
        lead.layout,
        configurationUrl,
      ]
        .filter(Boolean)
        .join('\n');

      // Test users' leads are marked so reps can tell them apart.
      const visualizationDetails = getPageVariable('digitalPrintPreview')
        ? { ...lead, preview: DIGITAL_PRINT_TOOL_NAME }
        : lead;

      this.storeRequestInfo({
        toolName: DIGITAL_PRINT_TOOL_NAME,
        description,
        image: {
          url: configuredImageUrl || lead.imageUrl,
          imgObj,
          description: analyticsLabel,
        },
        visualization: {
          configurationUrl,
          materialType: lead.material,
          opacity: lead.opacity,
          panelOrientation: lead.layout,
          imageName: imageName || analyticsLabel,
          imageUrl: lead.imageUrl,
          configuredImageUrl: configuredImageUrl || '',
          customerSuppliedImageUrl: customerSuppliedImageUrl || '',
          visualizationDetails,
        },
      });

      handleAdobeNavEvent({
        navString: `NAV|${DIGITAL_PRINT_TOOL_NAME}|Request a Quote`,
        destinationUrl: url,
        event,
      });
    } finally {
      this._quoting = false;
      done?.();
    }
  }

  get hideDownloadCta() {
    const image = this.selectedCarouselImage;
    if (image.is_video || image.is_spinshot) return true;
    if (image.is_viewer_3d) {
      return !this._viewer?.supports(VIEWER_CAPABILITIES.capture);
    }
    // A customer's own file has nothing to download back.
    if (image.is_digital_print) return !image.full_resolution_src;
    return false;
  }

  get downloadCtaLabel() {
    const image = this.selectedCarouselImage;
    if (image.is_viewer_3d) return 'Download 3D Image';
    if (image.is_designer) return 'Download Configured Image';
    if (image.is_digital_print) return 'Download Source Image';
    return 'Download Full Resolution Image';
  }

  async downloadViewerImage(file) {
    if (this._exporting || !this._viewer) return;
    this._exporting = true;
    try {
      const blob = await this._viewer.capture({
        format: 'png',
        background: 'canvas',
        scale: 2,
      });
      downloadBlob(blob, file.filename);
    } catch (error) {
      logErrorToSentry(error);
    } finally {
      this._exporting = false;
    }
  }

  willUpdate(changedProperties) {
    if (typeof super.willUpdate === 'function')
      super.willUpdate(changedProperties);

    if (
      this._carouselZoomEnabled &&
      (changedProperties.has('_selectedCarouselImageId') ||
        !this.carouselZoomable)
    ) {
      this.endCarouselZoom();
    }
  }

  updated(changedProperties) {
    if (typeof super.updated === 'function') super.updated(changedProperties);

    // A rendered slide has no load event to size the carousel from.
    if (
      this._designerSlideNeedsMeasure &&
      this.selectedCarouselImage?.is_designer
    ) {
      this._designerSlideNeedsMeasure = false;
      this.carouselImageLoaded();
    }

    this.attachViewerFrame();
  }

  async requestMoreInfoClicked(event, cta = event.detail?.cta) {
    if (event.detail?.digitalPrint) {
      return this.requestQuoteForDigitalPrint(event);
    }
    if (event.detail?.lead) return this.requestQuoteForDesign(event);

    const imageUrl = this._camera1ImageUrl
      ? this._camera1ImageUrl
      : this.imagebossUrl(this._carouselImagesArray[0][1].src, null, 700);

    const textDescription =
      this.hasSelectedCustomPattern || this._camera1ImageUrl
        ? this.thumbnailDescription
        : `${this.blockLabel} - ${this.headerStr}`;

    const sessionCookieData = {
      description: textDescription,
      image: {
        url: imageUrl,
        description: this.thumbnailDescription,
      },
    };

    window.sessionStorage.setItem(
      'requestInfoData',
      JSON.stringify(sessionCookieData)
    );

    const cleanName = adobeCleanString(cta);
    const label = adobeCleanString(`${this.blockLabel} - ${this.headerStr}`);
    handleAdobeNavEvent({
      navString: `NAV|${cleanName}|${label}`,
      destinationUrl: event.detail?.url,
      event,
    });
  }

  // Request a Quote from the pattern designer: the quote form gets the
  // design as text, the configured panel as an image, and the visualization
  // lead payload it posts on submit.
  async requestQuoteForDesign(event) {
    const {
      lead,
      analyticsLabel,
      image,
      imageUrl,
      selection,
      url,
    } = event.detail;
    const configurationUrl = window.location.href;

    const description = [
      `${lead.pattern} ${lead.variant}`,
      `${lead.printMethod}, ${lead.material}`,
      lead.etchTarget && `Etch: ${lead.etchTarget}`,
      lead.patternColor &&
        `Pattern: ${lead.patternColor} (${lead.patternOpacity})`,
      lead.backgroundColor &&
        `Background: ${lead.backgroundColor} (${lead.backgroundOpacity})`,
      configurationUrl,
    ]
      .filter(Boolean)
      .join('\n');

    // The configured panel travels as a data URL, which the quote form
    // attaches to the request as a file. Small and on white: session
    // storage holds a few MB at most, and reps get a printable image.
    let imgObj = null;
    try {
      const canvas = await renderToCanvas(imageUrl, selection, {
        width: QUOTE_IMAGE_WIDTH,
        height: QUOTE_IMAGE_WIDTH * 2,
        backdrop: '#ffffff',
      });
      imgObj = canvas.toDataURL('image/png');
    } catch (error) {
      logErrorToSentry(error);
    }

    this.storeRequestInfo({
      description,
      image: { url: imageUrl, imgObj, description: analyticsLabel },
      visualization: {
        configurationUrl,
        materialType: lead.material,
        opacity: lead.patternOpacity || 'not applicable',
        panelOrientation: 'not applicable',
        imageName: analyticsLabel,
        imageUrl: image?.full_resolution_src || imageUrl,
        configuredImageUrl: image?.full_resolution_src || imageUrl,
        visualizationDetails: lead,
      },
    });

    handleAdobeNavEvent({
      navString: 'NAV|Graphic Pattern Designer|Request a Quote',
      destinationUrl: url,
      event,
    });
  }

  // Falls back to the text-only handoff if the image pushes session storage
  // over its quota.
  storeRequestInfo(data) {
    try {
      window.sessionStorage.setItem('requestInfoData', JSON.stringify(data));
    } catch (error) {
      logErrorToSentry(error);
      window.sessionStorage.setItem(
        'requestInfoData',
        JSON.stringify({ ...data, image: { ...data.image, imgObj: null } })
      );
    }
  }

  scrollToVisualize() {
    const scrollToVisualize = new CustomEvent('scrollToVisualize', {
      bubbles: true,
      composed: true,
    });

    const el = this.shadowRoot.getElementById('visualize');
    const y =
      el.getBoundingClientRect().top +
      window.pageYOffset -
      this.stickyHeaderOffset;

    el.dispatchEvent(scrollToVisualize);
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  // Room above a section scrolled into view, clearing the mobile sticky header.
  get stickyHeaderOffset() {
    let offset = 30;
    if (isMobileLayout()) {
      const stickyHeader = this.shadowRoot.getElementById(
        'sticky-hero-wrapper-mobile'
      );
      if (!!stickyHeader) {
        offset += stickyHeader?.getBoundingClientRect().height || 208;
      }
    }
    return offset;
  }

  openOrderSheetDialog() {
    this.selectCarouselImageFromDescription('panel');

    const openOrderSheetDialog = new CustomEvent('openOrderSheetDialog', {
      bubbles: true,
      composed: true,
    });

    document.dispatchEvent(openOrderSheetDialog);
  }

  selectCarouselImageFromDescription(term) {
    term = term.toLowerCase();
    const imageIndex = this._carouselImagesArray.findIndex(image => {
      const description = image[1]?.thumbnail_description?.toLowerCase() || '';
      return description.includes(term);
    });

    this._selectedCarouselImageId =
      typeof imageIndex === 'number' && imageIndex >= 0
        ? imageIndex
        : this._carouselImagesArray.length - 1;

    if (term === 'panel' && !this._isEstimate) {
      const options = {
        detail: {
          imageUrl: this._carouselImagesArray[this._selectedCarouselImageId],
        },
        bubbles: true,
        composed: true,
      };

      document.dispatchEvent(new CustomEvent('panel-image-selected', options));
    }
  }

  renderBarrel(barrel) {
    // Do not render barrels that are told to be hidden elsewhere
    if (barrel.hide) return;

    // We don't want to show the fifth download if this feature is disabled
    // since it will be a link to a PDF we don't want to be available yet.
    // This check needs to be here since user-based enabling will happen.
    const isVisualizationBarrel = !!barrel.page_variants_path;
    const isOrderSheetsBarrel =
      barrel.panel_ordering === '1' && !barrel.estimate;
    const isEstimateBarrel = barrel.estimate;
    const isCutToOrderBarrel = barrel.direct_product_choices;
    const isCustomizeChoicesBarrel = barrel.choices && barrel.choices.length;
    const isGraphicPatternDesignerBarrel = !!designerContentFrom(barrel);

    if (isGraphicPatternDesignerBarrel) {
      // The designer carries its own quote and rep CTAs, like the visualizer.
      this._hasCustomizeBarrel = true;
      barrel.headerStr = this.headerStr;
      return html`
        <graphic-pattern-designer-barrel
          content=${JSON.stringify(barrel)}
          id="graphic-pattern-designer"
          class="print-none"
        ></graphic-pattern-designer-barrel>
      `;
    }

    if (digitalPrintContentFrom(barrel)) {
      this._hasCustomizeBarrel = true;
      return html`
        <digital-print-designer-barrel
          content=${JSON.stringify(barrel)}
          id="digital-print-designer"
          class="print-none"
        ></digital-print-designer-barrel>
      `;
    }

    // The Material Details barrel and all barrels for direct pages
    // should default to open
    barrel.expanded =
      barrel.title === 'Material Details' ||
      !!this.content.direct_spree_product_id;

    if (isVisualizationBarrel && !isCustomizeChoicesBarrel) {
      this._hasCustomizeBarrel = true;
      barrel.headerStr = this.headerStr;

      this.sendAnalyticsVisualizationAvailable();
      return html`
        <customize-product-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="visualize"
          class="print-none"
        ></customize-product-barrel>
      `;
    } else if (isCustomizeChoicesBarrel) {
      this._hasCustomizeBarrel = true;
      barrel.headerStr = this.headerStr;
      barrel.parentBlockId = this.content.id;
      return html`
        <customize-choices-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="visualize"
        ></customize-choices-barrel>
      `;
    } else if (isEstimateBarrel) {
      barrel.headerStr = this.headerStr;
      return html`
        <estimate-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
        ></estimate-barrel>
      `;
    } else if (barrel.customizeMaterialBarrel) {
      return html`
        <customize-material-barrel
          content=${JSON.stringify(barrel)}
          class="carousel-height"
        ></customize-material-barrel>
      `;
    } else if (isOrderSheetsBarrel && this.renderOrderSheetsBarrel) {
      this._hasOrderSheetBarrel = true;
      barrel.headerStr = `${this.headerStr} 48" x 96" Panel Order`;
      barrel.productHeader = this.content.header || '';
      barrel.productSubheader = this.content.subheader || '';
      barrel.dialogId = 'order-sheet';
      return html`
        <dialog-modal id="dialog-modal" content=${JSON.stringify(barrel)}>
        </dialog-modal>
      `;
    } else if (isCutToOrderBarrel) {
      barrel.headerStr = this.headerStr;
      barrel.imgUrl = this.heroImgUrl;
      return html`
        <cut-to-order-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
          id="cut-to-order"
        ></cut-to-order-barrel>
      `;
    } else if (
      !isVisualizationBarrel &&
      !isOrderSheetsBarrel &&
      !isEstimateBarrel &&
      !isCutToOrderBarrel
    ) {
      return html`
        <walter-barrel
          content=${JSON.stringify(barrel)}
          ?expanded=${barrel.expanded}
        ></walter-barrel>
      `;
    }
  }

  get renderOrderSheetsBarrel() {
    return this.panelOrderingAvailable(this._barrels);
  }

  // Takes the list because block data is built before this._barrels is set.
  panelOrderingAvailable(barrels) {
    const orderSheetsBarrel = barrels.find(
      barrel => barrel.panel_ordering === '1'
    );
    if (orderSheetsBarrel) {
      // Check that the barrel has the required information before rendering
      return (
        orderSheetsBarrel.pdma_gauges?.length > 0 &&
        orderSheetsBarrel.my3form_laminates?.length > 0 &&
        !orderSheetsBarrel.estimate
      );
    } else {
      return false;
    }
  }

  get blockLabel() {
    // The designer names the selected pattern variant above the header.
    return this._designerLabel || this.content.label || '';
  }

  get productId() {
    if (this.content.direct_spree_product_id)
      return this.content.direct_spree_product_id;
    if (!this._availableSampleTypes) return false;

    return this.content.sample_types[this.availableSampleTypes[0]].product_id;
  }

  get productVariantId() {
    if (this.content.direct_spree_variant_id)
      return this.content.direct_spree_variant_id;
    if (!this._availableSampleTypes) return false;

    return this.content.sample_types[this.availableSampleTypes[0]].variant_id;
  }

  get selectedSampleTypeKey() {
    return this._availableSampleTypes && this.availableSampleTypes[0];
  }

  get selectedSampleTypeProductGauge() {
    return this.selectedSampleType && this.selectedSampleType.product_gauge;
  }

  get selectedSampleType() {
    return (
      this.selectedSampleTypeKey &&
      this.content.sample_types[this.selectedSampleTypeKey]
    );
  }

  get selectedSampleTypeDisplayValue() {
    return this.selectedSampleType && this.selectedSampleType.sample_type_text;
  }

  get selectedSampleTypeImage() {
    return (
      !!this.selectedSampleType &&
      !!this.selectedSampleType.image_src &&
      this.selectedSampleType.image_src.replace(':dimensions', 'width/258')
    );
  }

  get selectedCarouselImage() {
    return this.carouselImageIsSet
      ? this._carouselImagesArray[this._selectedCarouselImageId][1]
      : '';
  }

  get carouselImageIsSet() {
    return (
      this._carouselImagesArray[this._selectedCarouselImageId] &&
      this._carouselImagesArray[this._selectedCarouselImageId][1]
    );
  }

  get headerStr() {
    let header = this.content.header || '';
    this.content.subheader
      ? (header = header + ' ' + this.content.subheader)
      : '';
    return this.withLineBreaks(header);
  }

  get descriptionStr() {
    return this.withLineBreaks(this.content.description) || '';
  }

  get variantsHeaderText() {
    if (this.blockLabel === '200.110') return 'Array Configurations';
    if (
      this.content.page_type === 'ProductCollection' ||
      this.content.page_type === 'Product'
    ) {
      return `${this.content.interlayer_name} Collection`;
    } else if (this._currentMaterialName) {
      return `${this.content.interlayer_name} ${this._currentMaterialName} Colors & Variants`;
    } else {
      return `${this.content.interlayer_name} Colors & Variants`;
    }
  }

  get returnUrl() {
    if (this.content.page_type === 'GraphicPattern')
      return this.graphicPatternsReturnUrl;

    const stored = window.sessionStorage.getItem('samplesReturnUrl');
    if (stored) return stored;

    if (!this.content.return_url) return false;

    const url = new URL(this.content.return_url, window.location.origin);
    const sampleType =
      new URLSearchParams(window.location.search).get('sample_type') || '';
    if (sampleType) url.pathname = `/samples/${sampleType}`;

    return `${url.pathname}${url.search}`;
  }

  // The catalog as the visitor left it, in the color they ended on here.
  // Until the designer reports a color, the catalog URL keeps its own.
  get graphicPatternsReturnUrl() {
    const stored = window.sessionStorage.getItem(
      GRAPHIC_PATTERNS_RETURN_URL_KEY
    );
    const url = new URL(
      stored || GRAPHIC_PATTERNS_CATALOG_PATH,
      window.location.origin
    );
    if (this._designerColor)
      url.searchParams.set('pattern', this._designerColor.replace('#', ''));

    return `${url.pathname}${url.search}`;
  }

  get thumbnailDescription() {
    return (
      this.withLineBreaks(this.selectedCarouselImage.thumbnail_description) ||
      ''
    );
  }

  get primaryCtaData() {
    const ctaData = this.buildWalterCtaData(this.content.cta || {});
    this.content.cta = ctaData;
    return ctaData;
  }

  get secondaryCtaData() {
    if (!this.content.secondary_cta_text) return null;
    return {
      text: this.content.secondary_cta_text,
      url: this.content.secondary_cta_url,
      isSampleOrderCta: this.isSampleOrderCta(this.content.secondary_cta_text),
      fullWidth: true,
    };
  }

  isSampleOrderCta(ctaData) {
    const text = typeof ctaData === 'object' ? ctaData.text : ctaData;
    return !!text?.toLowerCase()?.includes('sample');
  }

  isQuoteCta(ctaData) {
    return (
      !!ctaData?.url?.includes('/request-quote') ||
      !!ctaData?.text?.toLowerCase()?.includes('quote')
    );
  }

  get jesseDriverCtaData() {
    return JSON.stringify({
      text: 'Visualize Options',
      url: '#hardware-systems',
      fullWidth: true,
      isSampleOrderCta: false,
    });
  }

  get directPrimaryCtaData() {
    return JSON.stringify({
      text: 'Add to Cart',
      url: '',
      fullWidth: true,
    });
  }

  directQtyChanged(e) {
    if (e?.target?.value && Number(e?.target?.value) > 0) {
      this._addToCartQuantity = Number(e.target.value);
    }
  }

  get fallbackCtaData() {
    const fallbackCta =
      typeof this.content.fallback_cta === 'object'
        ? this.buildWalterCtaData(this.content.fallback_cta)
        : this.content.fallback_cta;
    return JSON.stringify(fallbackCta);
  }

  buildWalterCtaData(cta = {}) {
    const ctaData = { ...cta };

    ctaData.fullWidth = true;
    ctaData.isSampleOrderCta = this.isSampleOrderCta(ctaData?.text);

    if (ctaData.url?.toLowerCase().includes('visualizer')) {
      const cleanHeader = adobeCleanString(ctaData.text);
      const cleanCtaText = adobeCleanString(this.headerStr);
      ctaData.target = '_blank';
      ctaData.analyticsName = `NAV|${cleanHeader}|${cleanCtaText}`;
    }

    return ctaData;
  }

  // A customize barrel carries its own quote CTA, but nothing inside it
  // launches the visualizer, so that CTA stays.
  ctaHiddenByBarrel(cta) {
    return (
      this._hasCustomizeBarrel &&
      !cta?.url?.toLowerCase().includes('visualizer')
    );
  }

  get fallbackCtaHidden() {
    return !this.primaryCtaHidden;
  }

  get fallbackCtaDisabled() {
    return this.fallbackCtaHidden;
  }

  get fallbackCtaClickHandler() {
    if (this.content.fallback_cta?.url?.toLowerCase().includes('visualizer')) {
      return null;
    }

    return this.fallbackCtaClicked;
  }

  // A cta-button click carries no detail, so the CTA's text names the NAV.
  fallbackCtaClicked(event) {
    this.requestMoreInfoClicked(event, this.content.fallback_cta?.text);
  }

  // A material page's block mirrors the hero CTA. A product page's block
  // offers its material's sample, which is orderable only with a product id.
  get materialDetailsSampleCta() {
    if (!this._showMaterialDetailsBlockOnProductPage) {
      return {
        unavailable: this.primaryCtaHidden,
        disabled: this.primaryCtaDisabled,
        fallbackCtaData: this.fallbackCtaData,
      };
    }

    const { sampleData, fallbackCta } = this._materialDetailsBlockData;
    return {
      unavailable: !sampleData,
      disabled: false,
      fallbackCtaData: JSON.stringify(this.buildWalterCtaData(fallbackCta)),
    };
  }

  get interlayerVariantSlug() {
    return this.content.interlayer_variant_slug;
  }

  formatBarrels(barrels) {
    let formattedBarrels = barrels || [];

    formattedBarrels.forEach((b, index) => {
      b.expanded = true;
      b.order = index;

      // the customize-product-barrel is defaulted to open so it needs to be last
      if (!!b.page_variants_path) {
        b.order = formattedBarrels.length;
      }
    });

    formattedBarrels.sort((a, b) => a.order - b.order);

    if (this.useMaterialDetailsBlock) {
      this.setupMaterialDetailsBlock(formattedBarrels, null);
    }

    ///////////////////////////
    // Product Details Block //
    ///////////////////////////
    if (this.useProductDetailsBlock) {
      let productDetailsBlockData = [];
      // For the material details block we extract the following barrels from
      // the Walter and use them in the details block.
      const barrelsToExtract = [
        'Material Details',
        'Product Details',
        'Sustainability',
        'Downloads',
      ];
      barrelsToExtract.forEach(title => {
        const formattedBarrelsIndex = formattedBarrels.findIndex(
          obj => obj.title === title
        );
        if (formattedBarrelsIndex !== -1) {
          productDetailsBlockData.push(formattedBarrels[formattedBarrelsIndex]);
          formattedBarrels.splice(formattedBarrelsIndex, 1);
        }
      });

      // Format the exracted barrel data and make it available to the block
      if (productDetailsBlockData.length) {
        const sampleObj = Object.values(this.content.sample_types)[0];
        let sampleImgUrl = '';
        if (sampleObj) {
          sampleImgUrl = sampleObj?.image_src;
        } else {
          sampleImgUrl =
            this.sampleImageFromCarousel[1]?.src ||
            this._carouselImagesArray[0][1]?.src ||
            '';
        }
        sampleImgUrl = sampleImgUrl.replace(':dimensions', 'width/258');
        const fullPanelOrdering =
          this._hasOrderSheetBarrel && this.renderOrderSheetsBarrel;

        this._productDetailsBlockData = {
          productData: {
            product: this.productType,
            headerString: this.headerStr,
            imageUrl: sampleImgUrl,
            fullPanelOrdering,
          },
          barrelsData: productDetailsBlockData,
        };
      }
    }

    if (this.content.render_interlayer_id) {
      const {
        threekit_stages_path,
        threekit_hardware_finishes_path,
        render_interlayer_id,
      } = this.content;
      const sample_type_id =
        typeof this.content.sample_types === 'object'
          ? Object.values(this.content.sample_types)[0]?.id
          : null;
      const customizeMaterialBarrelData = {
        customizeMaterialBarrel: true,
        threekit_stages_path,
        threekit_hardware_finishes_path,
        render_interlayer_id,
        sample_type_id,
        productType: this.productType,
      };
      formattedBarrels.push(customizeMaterialBarrelData);
    }

    this._barrels = formattedBarrels;
  }

  checkAddMaterialDetailsBlock(json) {
    const pageVariantContent = json.page_variant_content;
    if (pageVariantContent && Object.keys(pageVariantContent).length > 0) {
      this._showMaterialDetailsBlockOnProductPage = true;
      this.setupMaterialDetailsBlock(null, json);
    }
  }

  setupMaterialDetailsBlock(formattedBarrels, dataFromEvent = null) {
    this._materialDetailsBlockData = null;
    let isMaterialOnProductPage = false;
    if (!formattedBarrels) {
      formattedBarrels = dataFromEvent.page_variant_content?.barrels || [];
      formattedBarrels.sort((a, b) => a.order - b.order);
      this._showMaterialDetailsBlockOnProductPage = true;
      isMaterialOnProductPage = true;
    }
    let materialDetailsBlockData = [];
    // For the material details block we extract the following barrels from
    // the Walter and use them in the details block.
    const barrelsToExtract = [
      'Material Details',
      'Sustainability',
      'Downloads',
    ];
    barrelsToExtract.forEach(title => {
      const formattedBarrelsIndex = formattedBarrels.findIndex(
        obj => obj.title === title
      );
      if (formattedBarrelsIndex !== -1) {
        materialDetailsBlockData.push(formattedBarrels[formattedBarrelsIndex]);
        formattedBarrels[formattedBarrelsIndex].hide = true;
      }
    });

    const sampleDataFromEvent = dataFromEvent
      ? Object.values(dataFromEvent.page_variant_content?.sample_types)[0]
      : null;
    // Format the exracted barrel data and make it available to the block
    if (materialDetailsBlockData.length) {
      const sampleObj = Object.values(this.content.sample_types)[0];
      let sampleImgUrl = '';
      if (sampleObj) {
        sampleImgUrl = sampleObj?.image_src;
      } else if (isMaterialOnProductPage) {
        sampleImgUrl = sampleDataFromEvent?.image_src;
      } else {
        sampleImgUrl =
          this.sampleImageFromCarousel[1]?.src ||
          this._carouselImagesArray[0][1]?.src ||
          '';
      }
      sampleImgUrl = sampleImgUrl?.replace(':dimensions', 'width/258') || '';
      // A material page builds this before its barrels render, so it reads
      // the list itself; on a product page the order-sheet dialog is the
      // host's, which has rendered by the time the event arrives.
      const fullPanelOrdering = isMaterialOnProductPage
        ? this._hasOrderSheetBarrel && this.renderOrderSheetsBarrel
        : this.panelOrderingAvailable(formattedBarrels);
      const sampleMaterialTypeText = isMaterialOnProductPage
        ? Object.keys(dataFromEvent.page_variant_content?.sample_types)[0]
        : '';
      const capitalizedSampleMaterialTypeText =
        sampleMaterialTypeText.charAt(0).toUpperCase() +
        sampleMaterialTypeText.slice(1);

      const material = isMaterialOnProductPage
        ? capitalizedSampleMaterialTypeText
        : this.materialType;
      const headerString = isMaterialOnProductPage
        ? dataFromEvent.page_variant_content?.header
        : this.headerStr;
      // A product page has no sample of its own, so this.productId is false
      // here; cart:add needs the material's Spree product, which an
      // unorderable sample's content leaves out.
      const sampleData = sampleDataFromEvent?.product_id
        ? {
            analyticsProductId: sampleDataFromEvent.product_id,
            productVariantId: sampleDataFromEvent.variant_id,
            headerStr: headerString,
            imageUrl: sampleImgUrl,
            sampleType: capitalizedSampleMaterialTypeText,
            gaugeStr: sampleDataFromEvent.product_gauge,
          }
        : null;

      this._materialDetailsBlockData = {
        materialData: {
          material,
          headerString,
          imageUrl: sampleImgUrl,
          fullPanelOrdering,
        },
        barrelsData: materialDetailsBlockData,
        ...(!!sampleData && { sampleData }),
        ...(isMaterialOnProductPage && {
          fallbackCta: dataFromEvent.page_variant_content?.fallback_cta,
        }),
      };
    }
    this.requestUpdate();
  }

  get sampleImageFromCarousel() {
    return (
      this._carouselImagesArray.find(image =>
        image[1].thumbnail_description.toLowerCase().includes('sample')
      ) || []
    );
  }

  get useMaterialDetailsBlock() {
    return (
      this.content.page_type === 'Interlayer' ||
      this.content.page_type === 'Color' ||
      this._showMaterialDetailsBlockOnProductPage
    );
  }

  get useProductDetailsBlock() {
    return (
      this.content.page_type === 'Product' ||
      this.content.page_type === 'ProductCollection'
    );
  }

  get variantsUrl() {
    return this.content.variants_url;
  }

  get hideImageSelections() {
    return this._carouselImagesArray.length <= 1;
  }

  get isFullPanelImage() {
    let isPanelImage = false;
    const selectedImage = this._carouselImagesArray[
      this._selectedCarouselImageId
    ];

    if (!!selectedImage) {
      isPanelImage =
        !!selectedImage[1]?.is_viewer_3d ||
        selectedImage[1]?.thumbnail_description
          ?.toLowerCase()
          ?.includes('panel');
    }

    return isPanelImage;
  }

  get heroImgUrl() {
    if (this.selectedCarouselImage.hasOwnProperty('carousel_src')) {
      return this.selectedCarouselImage.carousel_src;
    } else {
      // Image ratio for other images is 137/175
      // this adjusts the color chip images to cover that ratio from ImageBoss
      const width =
        this.content.interlayer_variant_slug === 'color-chip'
          ? (700 / 175) * 137
          : null;
      return this.imagebossUrl(this.selectedCarouselImage.src, width, 1000);
    }
  }
}
Walter.defineElement();
